import React, { useState, useEffect, useMemo, useCallback, useRef, Suspense, lazy } from 'react';
import ReactGA from 'react-ga4';
import { 
  LogOut, Loader2, ShieldCheck, X, ShoppingBag, Search, ArrowUpDown, ArrowUp, Eye, EyeOff, Menu
} from 'lucide-react';

// Import components
import ToastContainer from './components/ToastContainer';
import CartDrawer from './components/CartDrawer';
import ProductDetailView from './components/ProductDetailView';
import ProductCard from './components/ProductCard';

import ProductFormModal from './components/ProductFormModal';
import Navbar from './components/Navbar';
import AccountModal from './components/AccountModal';
import Footer from './components/Footer';
import SavedBadge, { useSavedBadge } from './components/SavedBadge';
import SuccessView from './components/SuccessView';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import useLocalStorage from './hooks/useLocalStorage';
import { supabase } from './utils/supabaseClient';
import { mapProductFromDb, mapProductToDb } from './utils/dataMapping';
import { normalizeString } from './utils/stringUtils';
import { INITIAL_PRODUCTS } from './data/initialProducts';

// Lazy load views for better performance
const HomeView = lazy(() => import('./components/HomeView'));
const CatalogView = lazy(() => import('./components/CatalogView'));
const CartView = lazy(() => import('./components/CartView'));
const CompareView = lazy(() => import('./components/CompareView'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const ClientsList = lazy(() => import('./components/ClientsList'));
const StockManagement = lazy(() => import('./components/StockManagement'));
const SuppliersView = lazy(() => import('./components/SuppliersView'));
const ArticlesBaseView = lazy(() => import('./components/ArticlesBaseView'));
const OrderTracking = lazy(() => import('./components/OrderTracking'));

// Initialisation Google Analytics
ReactGA.initialize('G-WXP1FVH8WC');

// --- GESTION DES DEVISES ---
const CURRENCIES = {
  EUR: { symbol: '€', rate: 1, label: 'Euro (EUR)' },
  USD: { symbol: '$', rate: 1.09, label: 'US Dollar (USD)' },
  GBP: { symbol: '£', rate: 0.86, label: 'British Pound (GBP)' },
  JPY: { symbol: '¥', rate: 162.50, label: 'Japanese Yen (JPY)' },
  AUD: { symbol: 'A$', rate: 1.65, label: 'Australian Dollar (AUD)' },
  CAD: { symbol: 'C$', rate: 1.47, label: 'Canadian Dollar (CAD)' },
  CHF: { symbol: 'CHF', rate: 0.95, label: 'Swiss Franc (CHF)' },
  CNY: { symbol: '元', rate: 7.85, label: 'Chinese Yuan (CNY)' },
  HKD: { symbol: 'HK$', rate: 8.50, label: 'Hong Kong Dollar (HKD)' },
  NZD: { symbol: 'NZ$', rate: 1.78, label: 'New Zealand Dollar (NZD)' }
};

export default function App() {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [clients, setClients] = useLocalStorage('dv_clients', []);
  // --- PRIX DE TRANSPORT ADMIN ---
  const [shippingPrices, setShippingPrices] = useLocalStorage('dv_shipping_prices', {
    standard: 25,
    express: 50
  });
  // --- PARAMÈTRES ADMINISTRATEUR ---
  const [adminCredentials, setAdminCredentials] = useLocalStorage('dv_admin_creds', {
    user: "admin",
    pass: "admin",
    recoveryEmail: "info@deepvision-hub.eu"
  });

  const [flashSaleSettings, setFlashSaleSettings] = useLocalStorage('dv_flash_sale_settings', {
    anticipationDays: 10,
    showCountdown: true
  });

  const [view, setView] = useLocalStorage('dv_current_view', 'home'); 
  const [adminTab, setAdminTab] = useLocalStorage('dv_admin_tab', 'dashboard'); // 'dashboard', 'stocks', 'suppliers', 'articles', 'clients'
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useLocalStorage('dv_client_search_term', '');
  
  const [currency, setCurrency] = useLocalStorage('dv_currency', 'EUR');

  const [selectedProduct, setSelectedProduct] = useLocalStorage('dv_selected_product', null);
  const [displayMode, setDisplayMode] = useLocalStorage('dv_display_mode', 'grid');
  const [isCartOpen, setIsCartOpen] = useLocalStorage('dv_is_cart_open', false);
  const [toasts, setToasts] = useState([]);
  
  const [editingProduct, setEditingProduct] = useState(null);
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [suppliers, setSuppliers] = useState([]); // Liste de tous les fournisseurs depuis Supabase

  // Supabase Auth State (declare early for user-scoped storage keys)
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [isGuest, setIsGuest] = useState(false);

  // Auth lifecycle flag (must be declared before any effects that read it)
  const [authReady, setAuthReady] = useState(false);

  const authedUserId = session?.user?.id || null;
  const userNamespace = authedUserId && !isGuest ? `_${authedUserId}` : '';

  const [buyerInfo, setBuyerInfo] = useLocalStorage(`dv_buyer_info${userNamespace}`, {
    name: '',
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    role: '',
    phone: '',
    address: '',
    postalCode: '',
    city: '',
    country: 'France',
    savedAddresses: []
  });

  // If a user logs in and we already have buyer info saved in the anonymous namespace,
  // migrate it to the user-scoped key (best-effort, non-destructive).
  useEffect(() => {
    if (!authReady) return;
    if (!authedUserId || isGuest) return;

    try {
      const anonRaw = localStorage.getItem('dv_buyer_info');
      const anon = anonRaw ? JSON.parse(anonRaw) : null;
      if (!anon || typeof anon !== 'object') return;

      const hasAnonData = Boolean(
        String(anon.email || '').trim() ||
        String(anon.name || '').trim() ||
        String(anon.company || '').trim() ||
        String(anon.address || '').trim() ||
        String(anon.city || '').trim() ||
        String(anon.postalCode || '').trim()
      );
      if (!hasAnonData) return;

      const hasUserData = Boolean(
        String(buyerInfo?.email || '').trim() ||
        String(buyerInfo?.name || '').trim() ||
        String(buyerInfo?.company || '').trim() ||
        String(buyerInfo?.address || '').trim() ||
        String(buyerInfo?.city || '').trim() ||
        String(buyerInfo?.postalCode || '').trim()
      );
      if (hasUserData) return;

      setBuyerInfo(prev => ({
        ...anon,
        ...prev,
        country: prev?.country || anon.country || 'France'
      }));
    } catch (e) {
      // ignore
    }
  }, [authReady, authedUserId, isGuest, buyerInfo, setBuyerInfo]);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [trackingOrderId, setTrackingOrderId] = useState(null);
  const [lastOrderRef, setLastOrderRef] = useState(null);
  const [lastBuyerName, setLastBuyerName] = useState(null);
  const [lastBuyerEmail, setLastBuyerEmail] = useState(null);
  const [lastOrderDate, setLastOrderDate] = useState(null);
  const [lastOrderShippingLabel, setLastOrderShippingLabel] = useState(null);
  const [lastOrderTotal, setLastOrderTotal] = useState(null);
  
  const [shippingFee, setShippingFee] = useLocalStorage('dv_shipping_fee', 0); // Valeur par défaut : Retrait sur place (0€)

  const prefersDark = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const [isDarkMode, setIsDarkMode] = useLocalStorage('dv_theme_bool', prefersDark);

  const [products, setProducts] = useState(INITIAL_PRODUCTS);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [navigationMode, setNavigationMode] = useState('direct'); // 'arrow' ou 'direct'
  const [totalProducts, setTotalProducts] = useState(0);
  const PRODUCTS_PER_PAGE = 30;
  
  // Toutes les catégories disponibles dans la base (indépendamment des filtres)
  const [allCategories, setAllCategories] = useState([]);

  // productCategories: liste complète des catégories pour le formulaire d'édition/création
  const productCategories = useMemo(() => {
    // Si on a chargé toutes les catégories depuis Supabase, les utiliser
    if (allCategories.length > 0) {
      return allCategories;
    }
    // Sinon, fallback sur les catégories des produits actuellement affichés
    const set = new Set();
    (Array.isArray(products) ? products : []).forEach((p) => {
      const c = String(p?.category || '').trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }, [allCategories, products]);

  useEffect(() => {
    // Nettoyage du localStorage pour éviter QuotaExceededError
    try {
      localStorage.removeItem('dv_products');
      // Supprimer tous les anciens caches qui causent QuotaExceededError
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.startsWith('supabase_products_cache') || 
          key.includes('_cache_public_p') ||
          key.includes('supabase_suppliers_cache') ||
          key.includes('supabase_assoc_cache')
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log(`🧹 ${keysToRemove.length} clés de cache nettoyées`);
    } catch (e) {
      console.error('Erreur nettoyage cache:', e);
    }
  }, []);

  // Les associations produit-fournisseur sont maintenant gérées directement dans Supabase

  const [cart, setCart] = useLocalStorage('dv_cart', []);
  const [favorites, setFavorites] = useLocalStorage(`dv_favs${userNamespace}`, []);
  const [recentlyViewed, setRecentlyViewed] = useLocalStorage(`dv_recent${userNamespace}`, []);
  const [orders, setOrders] = useLocalStorage(`dv_orders${userNamespace}`, []);

  // Keep latest values in refs to avoid re-subscribing auth listeners on every state change.
  const favoritesRef = useRef(favorites);
  const recentlyViewedRef = useRef(recentlyViewed);
  const buyerInfoRef = useRef(buyerInfo);
  useEffect(() => { favoritesRef.current = favorites; }, [favorites]);
  useEffect(() => { recentlyViewedRef.current = recentlyViewed; }, [recentlyViewed]);
  useEffect(() => { buyerInfoRef.current = buyerInfo; }, [buyerInfo]);
  
  // Supabase Auth (state + helpers)
  const didEnforceLoggedOutLandingRef = useRef(false);
  const lastAuthedUserIdRef = useRef(null);
  const userStateLoadedRef = useRef(false);
  const userStateSaveTimerRef = useRef(null);

  const USER_STATE_TABLE = 'user_state';

  const saveUserStateToSupabase = useCallback(async (userId, state) => {
    if (!userId) return;
    try {
      await supabase
        .from(USER_STATE_TABLE)
        .upsert([
          {
            user_id: userId,
            favorites: Array.isArray(state?.favorites) ? state.favorites : [],
            recently_viewed: Array.isArray(state?.recentlyViewed) ? state.recentlyViewed : [],
            buyer_info: state?.buyerInfo || null,
            updated_at: new Date().toISOString()
          }
        ], { onConflict: 'user_id' });
    } catch (e) {
      // Best-effort: if the table doesn't exist or RLS blocks, we keep localStorage behavior.
      // console.debug('saveUserStateToSupabase failed:', e?.message || e);
    }
  }, []);

  const loadUserStateFromSupabase = useCallback(async (userId) => {
    if (!userId) return null;
    try {
      const { data } = await supabase
        .from(USER_STATE_TABLE)
        .select('favorites, recently_viewed, buyer_info')
        .eq('user_id', userId)
        .maybeSingle();
      return data || null;
    } catch (e) {
      return null;
    }
  }, []);

  // SEO & Metadata Management
  useEffect(() => {
    const updateMeta = () => {
      const title = view === 'home' ? 'DEEPVISION HUB | Solutions Vision Industrielle' : 
                    view === 'catalog' ? 'Catalogue Produits | DEEPVISION HUB' :
                    view === 'admin' ? 'Administration | DEEPVISION HUB' :
                    `DEEPVISION HUB - ${view.charAt(0).toUpperCase() + view.slice(1)}`;
      
      document.title = title;
      
      // Update meta description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.setAttribute('name', 'description');
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute('content', 'DEEPVISION HUB - Votre partenaire expert en solutions de vision industrielle, caméras intelligentes et composants optiques de haute précision.');
    };

    updateMeta();
  }, [view]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (currentUser?.id) lastAuthedUserIdRef.current = currentUser.id;
      if (session) {
        setIsGuest(false);
        // Fill missing buyerInfo fields from Supabase metadata (never overwrite local edits)
        const meta = currentUser?.user_metadata;
        if (meta) {
          setBuyerInfo(prev => ({
            ...prev,
            name: prev.name || meta.full_name || '',
            company: prev.company || meta.company || '',
            email: currentUser.email || prev.email || '',
            phone: prev.phone || meta.phone || '',
            address: prev.address || meta.address || '',
            role: prev.role || meta.role || 'Client',
            city: prev.city || meta.city || '',
            postalCode: prev.postalCode || meta.postal_code || meta.postalCode || '',
            country: prev.country || meta.country || 'France'
          }));
        }
      }

      // Auth state is now known for this session
      setAuthReady(true);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (currentUser?.id) lastAuthedUserIdRef.current = currentUser.id;

      // Any auth event means auth state is now known
      setAuthReady(true);

      if (event === 'PASSWORD_RECOVERY') {
        setIsResettingPassword(true);
        setShowRecovery(true);
        setView('home');
      }

      if (event === 'SIGNED_OUT') {
        // Best-effort save before clearing UI state
        if (lastAuthedUserIdRef.current) {
          saveUserStateToSupabase(lastAuthedUserIdRef.current, {
            favorites: favoritesRef.current,
            recentlyViewed: recentlyViewedRef.current,
            buyerInfo: buyerInfoRef.current
          });
        }
        setView('home');
        setFavorites([]);
        setRecentlyViewed([]);
        setSelectedProduct(null);
      }

      if (session) {
        setIsGuest(false);
        const meta = currentUser?.user_metadata;
        if (meta) {
          setBuyerInfo(prev => ({
            ...prev,
            name: prev.name || meta.full_name || '',
            company: prev.company || meta.company || '',
            email: currentUser.email || prev.email || '',
            phone: prev.phone || meta.phone || '',
            address: prev.address || meta.address || '',
            role: prev.role || meta.role || 'Client',
            city: prev.city || meta.city || '',
            postalCode: prev.postalCode || meta.postal_code || meta.postalCode || '',
            country: prev.country || meta.country || 'France'
          }));
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [setBuyerInfo, saveUserStateToSupabase, setFavorites, setRecentlyViewed, setSelectedProduct, setView]);

  // When an authenticated user becomes available, try to restore state from Supabase.
  // This keeps data tied to the authenticated identity even across devices.
  useEffect(() => {
    const userId = session?.user?.id;
    if (!authReady) return;
    if (!userId || isGuest) return;

    // Reset guard when switching users
    userStateLoadedRef.current = false;

    (async () => {
      const data = await loadUserStateFromSupabase(userId);
      if (data) {
        if (Array.isArray(data.favorites)) setFavorites(data.favorites);
        if (Array.isArray(data.recently_viewed)) setRecentlyViewed(data.recently_viewed);
        if (data.buyer_info && typeof data.buyer_info === 'object') {
          setBuyerInfo(prev => ({
            ...prev,
            ...data.buyer_info
          }));
        }
      }
      userStateLoadedRef.current = true;
    })();
  }, [authReady, session?.user?.id, isGuest, loadUserStateFromSupabase, setFavorites, setRecentlyViewed, setBuyerInfo]);

  // Debounced autosave to Supabase for authenticated users.
  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId || isGuest) return;
    if (!authReady) return;
    if (!userStateLoadedRef.current) return;

    if (userStateSaveTimerRef.current) clearTimeout(userStateSaveTimerRef.current);
    userStateSaveTimerRef.current = setTimeout(() => {
      saveUserStateToSupabase(userId, { favorites, recentlyViewed, buyerInfo });
    }, 800);

    return () => {
      if (userStateSaveTimerRef.current) clearTimeout(userStateSaveTimerRef.current);
    };
  }, [authReady, session?.user?.id, isGuest, favorites, recentlyViewed, buyerInfo, saveUserStateToSupabase]);

  // Enforce landing page when the app starts with no authenticated user.
  // Only redirect if the persisted view is private (prevents "stuck" screens after logout)
  // while still allowing public navigation like catalog/home.
  useEffect(() => {
    if (!authReady) return;
    if (didEnforceLoggedOutLandingRef.current) return;

    if (!session && !isGuest) {
      const privateViews = new Set(['favorites', 'my-orders', 'order-tracking', 'admin']);
      if (privateViews.has(view)) {
        setView('home');
        setSelectedProduct(null);
      }

      // Clear user-scoped data for logged-out anonymous namespace.
      setFavorites([]);
      didEnforceLoggedOutLandingRef.current = true;
    }
  }, [authReady, session, isGuest, view, setView, setFavorites, setSelectedProduct]);

  const isLoggedIn = !!session || isGuest;
  const isAdmin = session?.user?.user_metadata?.role === 'admin';

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [authMode, setAuthMode] = useLocalStorage('dv_auth_mode', 'login'); // 'login' ou 'register'
  const [showRecovery, setShowRecovery] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoveryExpiry, setRecoveryExpiry] = useState(null);
  const [isChangingCreds, setIsChangingCreds] = useState(false);
  const [savedCreds, triggerCredsSaved] = useSavedBadge(2200);
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  const [searchQuery, setSearchQuery] = useLocalStorage('dv_search_query', '');
  const [orderSearchQuery, setOrderSearchQuery] = useLocalStorage('dv_order_search_query', '');
  const [orderSortBy, setOrderSortBy] = useLocalStorage('dv_order_sort_by', 'date-desc');
  const [orderFilterStatus, setOrderFilterStatus] = useLocalStorage('dv_order_filter_status', 'all'); // 'all', 'received', 'invoiced'
  const [filterCategory, setFilterCategory] = useLocalStorage('dv_filter_category', 'All');
  const [sortBy, setSortBy] = useLocalStorage('dv_sort_by', 'new');
  const [compareList, setCompareList] = useLocalStorage('dv_compare', []);

  // Optional hash navigation support (used by header links).
  useEffect(() => {
    const allowed = new Set(['home', 'catalog', 'favorites', 'my-orders', 'compare', 'cart', 'success']);

    const syncFromHash = () => {
      if (typeof window === 'undefined') return;
      const next = (window.location.hash || '').replace(/^#/, '');
      if (!allowed.has(next)) return;
      setView(prev => (prev === next ? prev : next));
    };

    // Initial sync (if user loads with a hash).
    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, [setView]);

  // Keep the URL hash in sync with internal navigation so refresh restores the current view.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const allowed = new Set(['home', 'catalog', 'favorites', 'my-orders', 'compare', 'cart', 'success']);
    if (!allowed.has(view)) return;

    const desired = `#${view}`;
    if ((window.location.hash || '') === desired) return;

    try {
      window.history.replaceState(null, '', desired);
    } catch (e) {
      // Fallback (should be rare)
      window.location.hash = desired;
    }
  }, [view]);

  const formatPrice = useCallback((priceInEur) => {
    const curr = CURRENCIES[currency] || CURRENCIES.EUR;
    const converted = (Number(priceInEur) || 0) * curr.rate;
    
    // Formatage spécifique pour JPY (pas de décimales)
    if (currency === 'JPY') {
      return `${Math.round(converted).toLocaleString('fr-FR')} ${curr.symbol}`;
    }
    
    const formatted = converted.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${formatted} ${curr.symbol}`;
  }, [currency]);

  const getEffectivePrice = useCallback((product) => {
    if (!product) return 0;
    
    // Vérification si la vente flash est active (avec dates optionnelles)
    let isFlashActive = product.isFlashSale;
    if (isFlashActive) {
      const now = new Date();
      if (product.flashSaleStartDate) {
        const start = new Date(product.flashSaleStartDate);
        if (now < start) isFlashActive = false;
      }
      if (product.flashSaleEndDate) {
        const end = new Date(product.flashSaleEndDate);
        if (now > end) isFlashActive = false;
      }
    }

    if (isFlashActive && product.flashSaleDiscount > 0) {
      return product.price * (1 - product.flashSaleDiscount / 100);
    }
    return product.price;
  }, []);

  // Helper pour savoir si un produit est actuellement en vente flash (pour l'affichage)
  const isFlashSaleActive = useCallback((product) => {
    if (!product || !product.isFlashSale) return false;
    const now = new Date();
    if (product.flashSaleStartDate) {
      const start = new Date(product.flashSaleStartDate);
      if (now < start) return false;
    }
    if (product.flashSaleEndDate) {
      const end = new Date(product.flashSaleEndDate);
      if (now > end) return false;
    }
    return true;
  }, []);

  // Helper pour savoir si une vente flash est à venir (pour le badge "Bientôt")
  const isFlashSaleComingSoon = useCallback((product) => {
    if (!product || !product.isFlashSale || !product.flashSaleStartDate) return false;
    const now = new Date();
    const start = new Date(product.flashSaleStartDate);
    if (now >= start) return false;

    // Vérifier si on est dans la période d'anticipation
    const anticipationMs = (flashSaleSettings.anticipationDays || 10) * 24 * 60 * 60 * 1000;
    return (start.getTime() - now.getTime()) <= anticipationMs;
  }, [flashSaleSettings.anticipationDays]);

  // Charger toutes les catégories disponibles (indépendamment des filtres)
  const fetchAllCategories = useCallback(async () => {
    try {
      console.log('🔄 Chargement de toutes les catégories...');
      const { data, error } = await supabase
        .from('products')
        .select('category')
        .not('category', 'is', null)
        .not('category', 'eq', '');
      
      if (error) throw error;
      
      const categoriesSet = new Set();
      (data || []).forEach(item => {
        const cat = String(item.category || '').trim();
        if (cat) categoriesSet.add(cat);
      });
      
      const sortedCategories = Array.from(categoriesSet).sort((a, b) => 
        a.localeCompare(b, 'fr', { sensitivity: 'base' })
      );
      
      setAllCategories(sortedCategories);
      console.log(`✅ ${sortedCategories.length} catégories chargées:`, sortedCategories);
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
    }
  }, []);

  // Fetch products, clients and orders from Supabase
  const fetchData = useCallback(async (silent = false, page = 1) => {
    if (!silent) setIsLoading(true);
    try {
      // Products - Toujours charger depuis Supabase (pas de cache pour éviter QuotaExceededError)
      const isAdminView = view === 'admin';
      
      let productsData;
      let totalCount = 0;
      
      console.log(`🔄 Chargement depuis Supabase (page ${page}, mode: ${navigationMode})...`);
      if (isAdminView) {
        const searchTerm = searchQuery || '';
        const ADMIN_PAGE_SIZE = 12; // Environ 12 articles visibles avec scroll raisonnable
        
        // Si recherche active
        if (searchTerm) {
          console.log(`🔍 Mode recherche admin : "${searchTerm}"`);
          
          const from = (page - 1) * ADMIN_PAGE_SIZE;
          // Précharger seulement si navigation par flèches (prévisible)
          const preloadSize = navigationMode === 'arrow' ? ADMIN_PAGE_SIZE : 0;
          const to = from + ADMIN_PAGE_SIZE + preloadSize - 1;
          
          let countQuery = supabase.from('products').select('*', { count: 'exact', head: true });
          let dataQuery = supabase.from('products').select('*').range(from, to).order('id', { ascending: false });
          
          const searchPattern = `%${searchTerm}%`;
          countQuery = countQuery.or(`label.ilike.${searchPattern},ref.ilike.${searchPattern},brand.ilike.${searchPattern},description.ilike.${searchPattern},category.ilike.${searchPattern}`);
          dataQuery = dataQuery.or(`label.ilike.${searchPattern},ref.ilike.${searchPattern},brand.ilike.${searchPattern},description.ilike.${searchPattern},category.ilike.${searchPattern}`);
          
          const { count } = await countQuery;
          totalCount = count || 0;
          
          const { data, error: productsError } = await dataQuery;
          if (productsError) throw productsError;
          productsData = data;
          console.log(`✅ ${data?.length || 0} produits chargés (${totalCount} trouvés, ${preloadSize > 0 ? 'avec préchargement' : 'sans préchargement'})`);
        } else {
          // Pas de recherche : pagination normale
          const from = (page - 1) * ADMIN_PAGE_SIZE;
          // Précharger seulement si navigation par flèches
          const preloadSize = navigationMode === 'arrow' ? ADMIN_PAGE_SIZE : 0;
          const to = from + ADMIN_PAGE_SIZE + preloadSize - 1;
          
          const { count } = await supabase.from('products').select('*', { count: 'exact', head: true });
          totalCount = count || 0;
          
          let query = supabase.from('products').select('*').range(from, to).order('id', { ascending: false });
          
          const { data, error: productsError } = await query;
          if (productsError) throw productsError;
          productsData = data;
          console.log(`✅ ${data?.length || 0} produits chargés (page ${page}/${Math.ceil(totalCount / ADMIN_PAGE_SIZE)}, ${preloadSize > 0 ? 'préchargé' : 'direct'})`);
        }
      } else {
        // Catalogue public : recherche + filtre + pagination côté Supabase
        const searchTerm = searchQuery || '';
        
        // Construction de la requête de base
        let countQuery = supabase.from('products').select('*', { count: 'exact', head: true });
        let dataQuery = supabase.from('products').select('*');
        
        // Filtre catégorie
        if (filterCategory && filterCategory !== 'All' && filterCategory !== 'flash') {
          countQuery = countQuery.eq('category', filterCategory);
          dataQuery = dataQuery.eq('category', filterCategory);
        }
        
        // Recherche textuelle (cherche dans label, ref, brand, description)
        if (searchTerm) {
          // Utiliser ilike qui est insensible à la casse
          // Chercher dans plusieurs colonnes avec OR
          const searchPattern = `%${searchTerm}%`;
          countQuery = countQuery.or(`label.ilike.${searchPattern},ref.ilike.${searchPattern},brand.ilike.${searchPattern},description.ilike.${searchPattern},category.ilike.${searchPattern}`);
          dataQuery = dataQuery.or(`label.ilike.${searchPattern},ref.ilike.${searchPattern},brand.ilike.${searchPattern},description.ilike.${searchPattern},category.ilike.${searchPattern}`);
        }
        
        // Compter le total (avec filtres appliqués)
        const { count } = await countQuery;
        totalCount = count || 0;
        
        // Charger la page avec pagination
        const from = (page - 1) * PRODUCTS_PER_PAGE;
        const to = from + PRODUCTS_PER_PAGE - 1;
        
        const { data, error: productsError } = await dataQuery.range(from, to);
        if (productsError) throw productsError;
        productsData = data;
        console.log(`✅ ${productsData?.length || 0} produits chargés (page ${page}/${Math.ceil(totalCount / PRODUCTS_PER_PAGE)}, total filtré: ${totalCount})`);
      }
      
      // Mettre à jour le total pour la pagination
      if (!isAdminView) {
        setTotalProducts(totalCount);
      }
      
      // Charger les fournisseurs et associations (sans cache pour éviter QuotaExceededError)
      let suppliers, associations;
      
      console.log('🔄 Chargement fournisseurs depuis Supabase...');
      const { data: suppliersData, error: suppliersError } = await supabase.from('suppliers').select('*');
      if (suppliersError) {
        console.error('Erreur chargement fournisseurs:', suppliersError);
        suppliers = [];
      } else {
        suppliers = suppliersData || [];
        console.log(`✅ ${suppliers.length} fournisseurs chargés`);
        // Stocker dans le state pour utilisation dans ProductFormModal
        setSuppliers(suppliers);
      }
      
      console.log('🔄 Chargement associations depuis Supabase...');
      const { data: assocData, error: assocError } = await supabase.from('product_suppliers').select('*');
      if (assocError) {
        console.error('Erreur chargement associations:', assocError);
        associations = [];
      } else {
        associations = assocData || [];
        console.log(`✅ ${associations.length} associations chargées`);
      }
      
      // Construire la Map des fournisseurs
      const suppliersMap = new Map();
      (suppliers || []).forEach(sup => {
        suppliersMap.set(sup.name, {
          name: sup.name,
          country: sup.country,
          url: sup.url,
          deliveryTime: sup.delivery_time,
          paymentMethod: sup.payment_method,
          disputeRate: sup.dispute_rate,
          contacts: sup.contacts || [],
          addresses: sup.addresses || [],
          notes: sup.notes
        });
      });
      
      // Construire la Map des associations par product_id
      const productSuppliersMap = new Map();
      (associations || []).forEach(assoc => {
        if (!productSuppliersMap.has(assoc.product_id)) {
          productSuppliersMap.set(assoc.product_id, []);
        }
        productSuppliersMap.get(assoc.product_id).push({
          supplierName: assoc.supplier_name,
          lastPrice: assoc.last_price || 0
        });
      });
      
      // Mapper les produits et ajouter les fournisseurs
      if (productsData) {
        const mappedProducts = productsData.map(p => {
          const product = mapProductFromDb(p);
          const linkedSuppliers = productSuppliersMap.get(String(product.id));
          
          if (linkedSuppliers && linkedSuppliers.length > 0) {
            product.suppliers = linkedSuppliers
              .map(link => {
                const supplierData = suppliersMap.get(link.supplierName);
                return supplierData ? { ...supplierData, lastPrice: link.lastPrice } : null;
              })
              .filter(s => s !== null);
          }
          
          return product;
        });
        
        setProducts(mappedProducts);
        console.log(`${mappedProducts.length} produits chargés avec leurs fournisseurs`);
      }

      // Admin only data
      if (isAdmin) {
        // Clients
        const { data: clientsData, error: clientsError } = await supabase
          .from('clients')
          .select('*')
          .order('last_seen', { ascending: false });
        if (clientsError) throw clientsError;
        if (clientsData) {
          setClients(clientsData.map(c => ({
            ...c,
            lastSeen: new Date(c.last_seen).toLocaleString('fr-FR'),
            createdAt: new Date(c.created_at).toLocaleString('fr-FR')
          })));
        }

        // Orders
        const { data: ordersData, error: ordersError } = await supabase
          .from('orders')
          .select('*, order_items(*)')
          .order('created_at', { ascending: false });
        if (ordersError) throw ordersError;
        if (ordersData) {
          setOrders(ordersData.map(o => ({
            id: o.ref,
            dbId: o.id,
            date: new Date(o.created_at).toLocaleDateString('fr-FR'),
            buyer: o.buyer_info,
            totalHT: o.total_ht,
            totalTTC: o.total_ht * 1.2,
            itemsCount: o.order_items?.length || 0,
            items: o.order_items?.map(item => ({
              ...item,
              selectedOptions: item.selected_options,
              reminderEligible: item.reminder_eligible
            })) || [],
            shipping: o.shipping_fee,
            shippingLabel: o.shipping_type === 'express' ? 'Express' : 'Standard',
            status: o.status
          })));
        }
      } else if (isLoggedIn) {
        // Fetch only current user's orders (prefer orders.user_id; fallback for legacy schemas)
        let myOrders = null;
        try {
          const { data, error } = await supabase
            .from('orders')
            .select('*, order_items(*)')
            .eq('user_id', user?.id)
            .order('created_at', { ascending: false });
          if (!error) myOrders = data;
        } catch (e) {
          // ignore
        }

        if (!myOrders) {
          try {
            const { data } = await supabase
              .from('orders')
              .select('*, order_items(*)')
              .eq('client_id', user?.id)
              .order('created_at', { ascending: false });
            myOrders = data;
          } catch (e) {
            // ignore
          }
        }

        if ((!myOrders || myOrders.length === 0) && user?.email) {
          try {
            const { data } = await supabase
              .from('orders')
              .select('*, order_items(*)')
              .contains('buyer_info', { email: user.email })
              .order('created_at', { ascending: false });
            myOrders = data;
          } catch (e) {
            // ignore
          }
        }
        
        if (myOrders) {
          setOrders(myOrders.map(o => ({
            id: o.ref,
            dbId: o.id,
            date: new Date(o.created_at).toLocaleDateString('fr-FR'),
            buyer: o.buyer_info,
            totalHT: o.total_ht,
            totalTTC: o.total_ht * 1.2,
            itemsCount: o.order_items?.length || 0,
            items: o.order_items?.map(item => ({
              ...item,
              selectedOptions: item.selected_options,
              reminderEligible: item.reminder_eligible
            })) || [],
            shipping: o.shipping_fee,
            shippingLabel: o.shipping_type === 'express' ? 'Express' : 'Standard',
            status: o.status
          })));
        }
      }

    } catch (error) {
      console.error('❌ Error fetching data:', error);
      console.error('Error details:', error.message, error.stack);
      showToast("Erreur lors du chargement des données Supabase", "error");
    } finally {
      if (!silent) setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, isAdmin, isLoggedIn, user?.id, searchQuery, filterCategory]);

  useEffect(() => {
    fetchData(false, currentPage);
  }, [fetchData, currentPage]);

  // Charger toutes les catégories au démarrage et en mode admin
  useEffect(() => {
    if (view === 'admin') {
      fetchAllCategories();
    }
  }, [view, fetchAllCategories]);

  const updateProducts = async (newProducts) => {
    setProducts(newProducts);
    
    // Sync all products to Supabase in bulk for better performance
    try {
      const dbProducts = newProducts.map(p => mapProductToDb(p));
      const { error } = await supabase
        .from('products')
        .upsert(dbProducts, { onConflict: 'ref' });
      
      if (error) throw error;
      
      // Recharger les catégories après la mise à jour
      await fetchAllCategories();
    } catch (error) {
      console.error('Error syncing products:', error.message);
      showToast("Erreur de synchronisation avec la base de données", "error");
    }
  };

  // Sécurité : s'assurer que les données critiques sont des tableaux
  // Cet effet doit s'exécuter uniquement au montage, on ignore l'avertissement de dépendances.
  useEffect(() => {
    if (!Array.isArray(products)) setProducts(INITIAL_PRODUCTS);
    if (!Array.isArray(cart)) setCart([]);
    if (!Array.isArray(favorites)) setFavorites([]);
    if (!Array.isArray(recentlyViewed)) setRecentlyViewed([]);
    if (!Array.isArray(orders)) setOrders([]);
    if (!Array.isArray(clients)) setClients([]);
    if (!Array.isArray(compareList)) setCompareList([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Uniquement au montage, ne pas ajouter de dépendances !

  // Ajout de gestion des erreurs pour les appels asynchrones
  const getEmailApiUrl = () => {
    const envUrl = process.env.REACT_APP_EMAIL_API_URL;

    if (typeof window !== 'undefined') {
      const host = window.location.hostname;
      const isLocalHost = host === 'localhost' || host === '127.0.0.1';
      const isHttpsPage = window.location.protocol === 'https:';

      if (envUrl) {
        const trimmed = String(envUrl).trim();
        const isLocalOverride = /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?\//i.test(trimmed);

        // Allow relative endpoints (recommended in production).
        if (/^(\/|\.|\?)/.test(trimmed)) {
          return trimmed;
        }

        // Prevent production from using external absolute URLs (common source of cert errors/mixed content).
        // Keep this strict: in production we want to hit same-origin `/send-email.php`.
        if (!isLocalHost && /^https?:\/\//i.test(trimmed)) {
          try {
            const parsed = new URL(trimmed);
            const isSameHost = parsed.host === window.location.host;

            // IPv4/IPv6 hosts are often used during setup but will fail TLS validation for the domain.
            const isIpv4 = /^\d{1,3}(?:\.\d{1,3}){3}$/.test(parsed.hostname);
            const isIpv6 = parsed.hostname.includes(':');

            if (!isSameHost || isIpv4 || isIpv6) {
              // ignore override
            } else if (isHttpsPage && parsed.protocol === 'http:') {
              parsed.protocol = 'https:';
              return parsed.toString();
            } else {
              return trimmed;
            }
          } catch {
            // ignore invalid URL
          }
        }

        // Prevent production from using a localhost override (would fail + mixed content on https).
        if (isLocalOverride && !isLocalHost) {
          // ignore
        } else if (isHttpsPage && /^http:\/\//i.test(trimmed)) {
          // Avoid mixed content on https pages.
          return trimmed.replace(/^http:\/\//i, 'https://');
        } else {
          return trimmed;
        }
      }

      if (isLocalHost) return 'http://localhost:3001/api/send-email';
    }

    if (envUrl) return String(envUrl).trim();

    // Production default (Hostinger): CRA copies public/send-email.php to the site root.
    return '/send-email.php';
  };

  const getAdminProductsApiUrl = () => {
    const envUrl = process.env.REACT_APP_ADMIN_PRODUCTS_API_URL;

    if (typeof window !== 'undefined') {
      const host = window.location.hostname;
      const isLocalHost = host === 'localhost' || host === '127.0.0.1';
      const isHttpsPage = window.location.protocol === 'https:';

      if (envUrl) {
        const trimmed = String(envUrl).trim();
        if (/^(\/|\.|\?)/.test(trimmed)) return trimmed;
        if (isHttpsPage && /^http:\/\//i.test(trimmed)) return trimmed.replace(/^http:\/\//i, 'https://');
        return trimmed;
      }

      // Local dev default: Node server (npm run server)
      if (isLocalHost) return 'http://localhost:3001/api/admin/products';
    }

    if (envUrl) return String(envUrl).trim();

    // Production default (Hostinger): this PHP file is deployed at site root.
    return '/admin-products.php';
  };

  const adminProductsRequest = useCallback(async (payload) => {
    const url = getAdminProductsApiUrl();
    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-User': adminCredentials?.user || '',
        'X-Admin-Pass': adminCredentials?.pass || ''
      },
      body: JSON.stringify(payload)
    });

    const text = await resp.text();
    let json = null;
    try { json = text ? JSON.parse(text) : null; } catch { json = null; }

    if (!resp.ok) {
      const msg = (json && (json.error?.message || json.error)) ? (json.error?.message || json.error) : (text || `HTTP ${resp.status}`);
      throw new Error(String(msg));
    }

    if (json && json.ok === false) {
      throw new Error(String(json.error || 'Erreur API admin'));
    }

    return json;
  }, [adminCredentials?.user, adminCredentials?.pass]);

  const diagnoseEmailApi = async (emailApiUrl) => {
    try {
      const healthResp = await fetch(emailApiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      const contentType = (healthResp.headers.get('content-type') || '').toLowerCase();
      const text = await healthResp.text();
      const trimmed = (text || '').trim();

      if (contentType.includes('application/json')) {
        try {
          const json = JSON.parse(text);
          if (json && json.configured === false) return "SMTP non configuré côté serveur";
          if (json && json.configured === true) return "Serveur joignable (SMTP configuré)";
          return "Réponse JSON inattendue";
        } catch {
          return "JSON invalide renvoyé par le serveur";
        }
      }

      if (trimmed.startsWith('<?php') || trimmed.includes('<?php')) {
        return "Le serveur renvoie le fichier PHP brut (PHP non exécuté)";
      }

      if (healthResp.status === 405) return "Le serveur refuse la méthode GET (405)";
      if (!healthResp.ok) return `Health check HTTP ${healthResp.status}`;
      if (contentType) return `Réponse inattendue (${contentType})`;
      return "Réponse inattendue";
    } catch (e) {
      return null;
    }
  };

  const sendRecoveryEmail = async (email, code) => {
    try {
      // Appel au serveur SMTP local (Hostinger)
      const response = await fetch(getEmailApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          from: 'DeepVision Security <info@deepvision-hub.eu>',
          to: [email],
          subject: 'Votre code de récupération DeepVision',
          html: `
            <div style="font-family: sans-serif; padding: 20px; color: #1e293b;">
              <h2 style="color: #2563eb;">Code de sécurité</h2>
              <p>Vous avez demandé une récupération d'accès pour votre console DeepVision.</p>
              <div style="background: #f1f5f9; padding: 20px; border-radius: 12px; text-align: center; font-size: 32px; font-weight: bold; letter-spacing: 5px; margin: 20px 0;">
                ${code}
              </div>
              <p style="color: #64748b; font-size: 14px;">Ce code est valable pendant 60 secondes.</p>
              <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 20px 0;" />
              <p style="font-size: 12px; color: #94a3b8;">Si vous n'êtes pas à l'origine de cette demande, ignorez cet email.</p>
            </div>
          `
        })
      });
      return response.ok;
    } catch (error) {
      console.error("Erreur envoi email:", error);
      return false;
    }
  };

  useEffect(() => {
    const loadScripts = async () => {
      const load = (src) => new Promise(r => {
        const s = document.createElement('script');
        s.src = src; s.onload = r; document.head.appendChild(s);
      });
      if (!window.jspdf) await load('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
      if (!window.Papa) await load('https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.4.1/papaparse.min.js');
    };
    loadScripts();
  }, []);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Suivi des pages GA4
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: view, title: view });
  }, [view]);

  // Gestion du partage via URL
  useEffect(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const productId = params.get('product');
    if (productId && Array.isArray(products)) {
      const product = products.find(p => String(p.id) === String(productId));
      if (product) {
        setSelectedProduct(product);
        setView('product-detail');
        // Nettoyer l'URL sans recharger la page
        if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
          window.history.replaceState({}, '', window.location.pathname);
        }
      }
    }
  }, [products, setSelectedProduct, setView]);

  const showToast = useCallback((message, type = 'success', durationMs = 5000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    const safeDuration = Number.isFinite(durationMs) ? Math.max(1500, durationMs) : 5000;
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), safeDuration);
  }, []);

  const saveClient = useCallback(async (info) => {
    if (!info || !info.email) return;
    
    try {
      // Vérifier si un client existe avec cet ID ou cet email
      let existingClient = null;
      
      if (info.id) {
        const { data } = await supabase
          .from('clients')
          .select('id')
          .eq('id', info.id)
          .single();
        existingClient = data;
      } else {
        const { data } = await supabase
          .from('clients')
          .select('id')
          .eq('email', info.email)
          .single();
        existingClient = data;
      }

      const clientData = {
        first_name: info.firstName || '',
        last_name: info.lastName || '',
        name: info.name || `${info.firstName || ''} ${info.lastName || ''}`.trim(),
        email: info.email,
        role: info.role || 'Client',
        payment_terms: info.paymentTerms || 'Comptant',
        payment_method: info.paymentMethod || '',
        notes: info.notes || '',
        last_seen: new Date().toISOString()
      };

      // Companies array
      if (info.companies?.length) {
        clientData.companies = info.companies.filter(c => c?.trim());
      }
      
      // Secondary emails array
      const emailsField = info.secondaryEmails || info.secondary_emails;
      if (emailsField?.length) {
        clientData.secondary_emails = emailsField.filter(e => e?.trim());
      }

      // Phone numbers
      if (info.phone) clientData.phone = String(info.phone);
      
      if (info.phones?.length) {
        clientData.phone_numbers = info.phones
          .filter(p => p?.value?.trim())
          .map(p => ({ value: String(p.value), type: String(p.type || 'Portable') }));
      }

      // Addresses
      const validAddresses = info.addresses?.length 
        ? info.addresses
            .filter(addr => addr?.street || addr?.address)
            .map(addr => ({
              type: String(addr.type || 'Travail'),
              street: String(addr.street || addr.address || ''),
              postalCode: String(addr.postalCode || ''),
              city: String(addr.city || ''),
              country: String(addr.country || 'France')
            }))
        : info.address 
        ? [{
            type: 'Travail',
            street: String(info.address),
            postalCode: String(info.postalCode || ''),
            city: String(info.city || ''),
            country: String(info.country || 'France')
          }]
        : [];

      if (validAddresses.length) {
        clientData.addresses = validAddresses;
        const firstAddr = validAddresses[0];
        clientData.address = firstAddr.street;
        clientData.postal_code = firstAddr.postalCode;
        clientData.city = firstAddr.city;
        clientData.country = firstAddr.country;
      }

      // Billing address
      clientData.billing_address = String(info.billingAddress || info.address || '');
      clientData.billing_postal_code = String(info.billingPostalCode || info.postalCode || '');
      clientData.billing_city = String(info.billingCity || info.city || '');
      clientData.billing_country = String(info.billingCountry || info.country || 'France');

      if (existingClient) {
        const { error: updateError } = await supabase
          .from('clients')
          .update(clientData)
          .eq('id', existingClient.id);
        
        if (updateError) {
          console.error('Update error:', updateError);
          alert(`Erreur de mise à jour: ${updateError.message || JSON.stringify(updateError)}`);
          throw new Error(updateError.message || JSON.stringify(updateError));
        }
      } else {
        const { error: insertError } = await supabase
          .from('clients')
          .insert([{ ...clientData, created_at: new Date().toISOString() }]);
        
        if (insertError) {
          console.error('Insert error:', insertError);
          alert(`Erreur d'insertion: ${insertError.message || JSON.stringify(insertError)}`);
          throw new Error(insertError.message || JSON.stringify(insertError));
        }
      }

      // Refresh local clients list
      const { data: allClients } = await supabase.from('clients').select('*').order('last_seen', { ascending: false });
      if (allClients) {
        setClients(allClients.map(c => ({
          ...c,
          lastSeen: new Date(c.last_seen).toLocaleString('fr-FR'),
          createdAt: new Date(c.created_at).toLocaleString('fr-FR')
        })));
      }
    } catch (error) {
      console.error('Error saving client to Supabase:', error.message);
      throw error;
    }
  }, [setClients]);

  const saveClients = useCallback(async (newClients) => {
    if (!Array.isArray(newClients) || newClients.length === 0) return;

    try {
      const dbClients = newClients.map(c => ({
        name: c.name || c.nom || '',
        company: c.company || c.societe || '',
        email: c.email,
        role: c.role || 'Client',
        phone: c.phone || c.telephone || '',
        address: c.address || (c.addresses?.[0]?.value || ''),
        last_seen: new Date().toISOString(),
        created_at: c.createdAt ? new Date(c.createdAt).toISOString() : new Date().toISOString()
      }));

      const { error } = await supabase
        .from('clients')
        .upsert(dbClients, { onConflict: 'email' });

      if (error) throw error;

      // Refresh local clients list
      const { data: allClients } = await supabase.from('clients').select('*').order('last_seen', { ascending: false });
      if (allClients) {
        setClients(allClients.map(c => ({
          ...c,
          lastSeen: new Date(c.last_seen).toLocaleString('fr-FR'),
          createdAt: new Date(c.created_at).toLocaleString('fr-FR')
        })));
      }
    } catch (error) {
      console.error('Error saving clients to Supabase:', error.message);
      showToast("Erreur lors de la sauvegarde des clients", "error");
    }
  }, [setClients, showToast]);

  const updateOrder = useCallback(async (orderId, updates) => {
    try {
      // Map updates to DB format if needed
      const dbUpdates = {};
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.invoicePDF !== undefined) dbUpdates.invoice_pdf = updates.invoicePDF;
      if (updates.invoiceUploadedAt !== undefined) dbUpdates.invoice_uploaded_at = updates.invoiceUploadedAt;

      const { error } = await supabase
        .from('orders')
        .update(dbUpdates)
        .eq('ref', orderId); // Using ref as the identifier in the UI

      if (error) throw error;

      // Refresh local orders list
      const { data: ordersData } = await supabase
        .from('orders')
        .select('*, order_items(*)')
        .order('created_at', { ascending: false });

      if (ordersData) {
        setOrders(ordersData.map(o => ({
          id: o.ref,
          dbId: o.id,
          date: new Date(o.created_at).toLocaleDateString('fr-FR'),
          buyer: o.buyer_info,
          totalHT: o.total_ht,
          totalTTC: o.total_ht * 1.2,
          itemsCount: o.order_items?.length || 0,
          items: o.order_items?.map(item => ({
            ...item,
            selectedOptions: item.selected_options,
            reminderEligible: item.reminder_eligible
          })) || [],
          shipping: o.shipping_fee,
          shippingLabel: o.shipping_type === 'express' ? 'Express' : 'Standard',
          status: o.status
        })));
      }
    } catch (error) {
      console.error('Error updating order in Supabase:', error.message);
    }
  }, [setOrders]);

  // Backfill clients from existing orders (best-effort)
  React.useEffect(() => {
    try {
      const safeOrders = Array.isArray(orders) ? orders : [];
      safeOrders.forEach(o => {
        const b = o?.buyer;
        if (!b) return;
        if (typeof b === 'object' && (b.email || b.name)) {
          saveClient({
            name: b.name || b.fullname || '',
            company: b.company || '',
            email: b.email || '',
            role: b.role || '',
            phone: b.phone || '',
            address: b.address || ''
          });
        }
      });
    } catch (error) {
      console.error('Error backfilling clients:', error);
    }
  }, [orders, saveClient]);

  // --- GESTION DES FOURNISSEURS AVEC SUPABASE ---
  const saveSupplier = useCallback(async (supplierData, productIds = [], replaceMode = false) => {
    try {
      const dbSupplier = {
        name: String(supplierData.name || ''),
        country: String(supplierData.country || 'France'),
        url: String(supplierData.url || ''),
        delivery_time: String(supplierData.deliveryTime || ''),
        payment_method: String(supplierData.paymentMethod || ''),
        dispute_rate: Number(supplierData.disputeRate || 0),
        contacts: Array.isArray(supplierData.contacts) ? supplierData.contacts : [],
        addresses: Array.isArray(supplierData.addresses) ? supplierData.addresses : [],
        notes: String(supplierData.notes || '')
      };

      // Vérifier si le fournisseur existe déjà (sans .single() pour éviter les erreurs)
      const { data: existing, error: checkError } = await supabase
        .from('suppliers')
        .select('id')
        .eq('name', dbSupplier.name)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existing) {
        // Mettre à jour le fournisseur existant
        const { error: updateError } = await supabase
          .from('suppliers')
          .update(dbSupplier)
          .eq('name', dbSupplier.name);

        if (updateError) throw updateError;
        console.log('Fournisseur mis à jour dans Supabase:', dbSupplier.name);
      } else {
        // Créer un nouveau fournisseur
        const { error: insertError } = await supabase
          .from('suppliers')
          .insert([dbSupplier]);

        if (insertError) throw insertError;
        console.log('Fournisseur créé dans Supabase:', dbSupplier.name);
      }

      // Sauvegarder les associations produit-fournisseur
      if (replaceMode) {
        // MODE REMPLACEMENT (édition du fournisseur): supprimer toutes les anciennes associations puis créer les nouvelles
        console.log(`🔄 Mode remplacement: suppression des anciennes associations pour "${dbSupplier.name}"...`);
        const { error: deleteError } = await supabase
          .from('product_suppliers')
          .delete()
          .eq('supplier_name', dbSupplier.name);
        
        if (deleteError) {
          console.error('❌ Erreur suppression anciennes associations:', deleteError);
        } else {
          console.log(`✅ Anciennes associations supprimées pour "${dbSupplier.name}"`);
        }
        
        // Créer les nouvelles associations si des produits sont sélectionnés
        if (Array.isArray(productIds) && productIds.length > 0) {
          console.log(`💾 Création de ${productIds.length} nouvelles associations...`);
          const associations = productIds.map(productId => ({
            product_id: String(productId),
            supplier_name: dbSupplier.name,
            last_price: 0
          }));
          
          const { error: linkError } = await supabase
            .from('product_suppliers')
            .insert(associations);

          if (linkError) {
            console.error('❌ Erreur création associations:', linkError);
          } else {
            console.log(`✅ ${productIds.length} associations créées pour ${dbSupplier.name}`);
          }
        }
      } else if (Array.isArray(productIds) && productIds.length > 0) {
        // MODE AJOUT (modal "Sans Fournisseur"): ajouter aux associations existantes
        console.log(`💾 Mode ajout: sauvegarde batch de ${productIds.length} associations pour "${dbSupplier.name}"...`);
        
        const associations = productIds.map(productId => ({
          product_id: String(productId),
          supplier_name: dbSupplier.name,
          last_price: 0
        }));
        
        const { error: linkError } = await supabase
          .from('product_suppliers')
          .upsert(associations, { onConflict: 'product_id,supplier_name' });

        if (linkError) {
          console.error('❌ Erreur liaison batch produit-fournisseur:', linkError);
        } else {
          console.log(`✅ ${productIds.length} associations ajoutées pour ${dbSupplier.name}`);
        }
      }

      // Recharger uniquement les fournisseurs et associations (plus rapide que fetchData complet)
      console.log('🔄 Rechargement des fournisseurs et associations...');
      
      const { data: suppliersData } = await supabase.from('suppliers').select('*');
      const { data: assocData } = await supabase.from('product_suppliers').select('*');
      
      const suppliers = suppliersData || [];
      const associations = assocData || [];
      
      // Construire la Map des fournisseurs
      const suppliersMap = new Map();
      suppliers.forEach(sup => {
        suppliersMap.set(sup.name, {
          name: sup.name,
          country: sup.country,
          url: sup.url,
          deliveryTime: sup.delivery_time,
          paymentMethod: sup.payment_method,
          disputeRate: sup.dispute_rate,
          contacts: sup.contacts || [],
          addresses: sup.addresses || [],
          notes: sup.notes
        });
      });
      
      // Construire la Map des associations par product_id
      const productSuppliersMap = new Map();
      associations.forEach(assoc => {
        if (!productSuppliersMap.has(assoc.product_id)) {
          productSuppliersMap.set(assoc.product_id, []);
        }
        productSuppliersMap.get(assoc.product_id).push({
          supplierName: assoc.supplier_name,
          lastPrice: assoc.last_price || 0
        });
      });
      
      // Mettre à jour uniquement les suppliers des produits
      setProducts(prevProducts => prevProducts.map(p => {
        const linkedSuppliers = productSuppliersMap.get(String(p.id));
        
        if (linkedSuppliers && linkedSuppliers.length > 0) {
          const suppliers = linkedSuppliers
            .map(link => {
              const supplierData = suppliersMap.get(link.supplierName);
              return supplierData ? { ...supplierData, lastPrice: link.lastPrice } : null;
            })
            .filter(s => s !== null);
          
          return { ...p, suppliers };
        } else {
          // Aucune association : retirer les suppliers
          return { ...p, suppliers: [] };
        }
      }));
      
      console.log(`✅ ${suppliers.length} fournisseurs et ${associations.length} associations rechargés`);
      showToast(`Fournisseur "${dbSupplier.name}" sauvegardé avec succès !`, "success");
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du fournisseur dans Supabase:', error);
      throw error;
    }
  }, [setProducts, showToast]);

  const deleteSupplier = useCallback(async (supplierName) => {
    try {
      const { error } = await supabase
        .from('suppliers')
        .delete()
        .eq('name', supplierName);

      if (error) throw error;
      console.log('Fournisseur supprimé de Supabase:', supplierName);
    } catch (error) {
      console.error('Erreur lors de la suppression du fournisseur dans Supabase:', error.message);
    }
  }, []);

  const syncSuppliersFromProducts = useCallback(async () => {
    // Extraire tous les fournisseurs uniques depuis les produits et les synchroniser avec Supabase
    try {
      const uniqueSuppliers = new Map();
      const associations = []; // Stocker toutes les associations produit-fournisseur
      
      products.forEach(product => {
        if (Array.isArray(product.suppliers)) {
          product.suppliers.forEach(supplier => {
            if (supplier.name) {
              // Ajouter le fournisseur unique
              if (!uniqueSuppliers.has(supplier.name)) {
                uniqueSuppliers.set(supplier.name, {
                  name: supplier.name,
                  country: supplier.country || 'France',
                  url: supplier.url || '',
                  deliveryTime: supplier.deliveryTime || '',
                  paymentMethod: supplier.paymentMethod || '',
                  disputeRate: supplier.disputeRate || 0,
                  contacts: supplier.contacts || [],
                  addresses: supplier.addresses || [],
                  notes: supplier.notes || ''
                });
              }
              
              // Ajouter l'association produit-fournisseur
              associations.push({
                product_id: String(product.id),
                supplier_name: supplier.name,
                last_price: supplier.lastPrice || 0
              });
            }
          });
        }
      });

      // 1. Sauvegarder chaque fournisseur unique dans Supabase
      for (const supplierData of uniqueSuppliers.values()) {
        await saveSupplier(supplierData);
      }

      // 2. Sauvegarder toutes les associations dans product_suppliers
      if (associations.length > 0) {
        const { error } = await supabase
          .from('product_suppliers')
          .upsert(associations, { onConflict: 'product_id,supplier_name' });
        
        if (error) {
          console.error('Erreur lors de la sauvegarde des associations:', error);
        } else {
          console.log(`${associations.length} associations sauvegardées`);
        }
      }

      console.log(`${uniqueSuppliers.size} fournisseurs et ${associations.length} associations synchronisés avec Supabase`);
    } catch (error) {
      console.error('Erreur lors de la synchronisation des fournisseurs:', error);
    }
  }, [products, saveSupplier]);



  const cartTotal = useMemo(() => (Array.isArray(cart) ? cart : []).reduce((sum, item) => sum + (item.price * item.quantity), 0), [cart]);
  const cartCount = useMemo(() => (Array.isArray(cart) ? cart : []).reduce((sum, item) => sum + item.quantity, 0), [cart]);

  const hasLowStock = useMemo(() => {
    if (!Array.isArray(products)) return false;
    return products.some(p => {
      if (p?.isMatrix) return Object.values(p.stockMap || {}).some(s => s <= 5);
      return (p?.stock || 0) <= 5;
    });
  }, [products]);

  const toggleDarkMode = () => setIsDarkMode(!isDarkMode);
  
  const handleShare = useCallback((e, product) => {
    if (e) e.stopPropagation();
    
    ReactGA.event({
      category: 'Social',
      action: 'Share Product',
      label: product.label
    });

    const shareData = {
      title: product.label,
      text: product.summary,
      url: `${window.location.origin}${window.location.pathname}?product=${product.id}`
    };

    if (navigator.share) {
      navigator.share(shareData)
        .then(() => showToast("Lien partagé !", "success"))
        .catch((err) => {
          if (err.name !== 'AbortError') {
            navigator.clipboard.writeText(shareData.url);
            showToast("Lien copié !", "success");
          }
        });
    } else {
      navigator.clipboard.writeText(shareData.url)
        .then(() => showToast("Lien copié !", "success"))
        .catch(() => showToast("Erreur lors de la copie", "error"));
    }
  }, [showToast]);

  const toggleFavorite = useCallback((e, product) => {
    if (e) e.stopPropagation();
    setFavorites(prev => {
      const isFav = prev.find(f => f.id === product.id);
      if (isFav) {
        showToast("Retiré des favoris", "info");
        return prev.filter(f => f.id !== product.id);
      } else {
        showToast("Ajouté aux favoris");
        return [...prev, product];
      }
    });
  }, [showToast, setFavorites]);

  const toggleFlashSale = useCallback(async (productId) => {
    let updatedProduct = null;
    setProducts(prev => prev.map(p => {
      if (p.id === productId) {
        updatedProduct = { ...p, isFlashSale: !p.isFlashSale };
        return updatedProduct;
      }
      return p;
    }));

    if (updatedProduct) {
      try {
        if (view === 'admin') {
          await adminProductsRequest({
            action: 'update',
            id: productId,
            data: { is_flash_sale: updatedProduct.isFlashSale }
          });
        } else {
          const { error } = await supabase
            .from('products')
            .update({ is_flash_sale: updatedProduct.isFlashSale })
            .eq('id', productId);
          if (error) throw error;
        }
        showToast("Statut Vente Flash mis à jour");
      } catch (error) {
        console.error('Error updating flash sale status:', error.message);
        showToast("Erreur de synchronisation", "error");
      }
    }
  }, [setProducts, showToast, view, adminProductsRequest]);

  const updateFlashDiscount = useCallback(async (productId, discount) => {
    setProducts(prev => prev.map(p => 
      p.id === productId ? { ...p, flashSaleDiscount: discount } : p
    ));

    try {
      if (view === 'admin') {
        await adminProductsRequest({
          action: 'update',
          id: productId,
          data: { flash_sale_discount: discount }
        });
      } else {
        const { error } = await supabase
          .from('products')
          .update({ flash_sale_discount: discount })
          .eq('id', productId);
        if (error) throw error;
      }
    } catch (error) {
      console.error('Error updating flash discount:', error.message);
    }
  }, [setProducts, view, adminProductsRequest]);

  const toggleCategoryFlashSale = useCallback(async (category, status, startDate = '', endDate = '', discount = 0) => {
    if (status && startDate && endDate && new Date(endDate) <= new Date(startDate)) {
      showToast("La date de fin doit être après la date de début", "error");
      return;
    }
    
    setProducts(prev => prev.map(p => 
      p.category === category ? { 
        ...p, 
        isFlashSale: status,
        flashSaleStartDate: status ? startDate : '',
        flashSaleEndDate: status ? endDate : '',
        flashSaleDiscount: status ? (discount || p.flashSaleDiscount) : p.flashSaleDiscount
      } : p
    ));

    try {
      const patch = {
        is_flash_sale: status,
        flash_sale_start_date: status ? startDate : null,
        flash_sale_end_date: status ? endDate : null,
        flash_sale_discount: status ? discount : 0
      };

      if (view === 'admin') {
        await adminProductsRequest({
          action: 'bulkUpdateByCategory',
          category,
          data: patch
        });
      } else {
        const { error } = await supabase
          .from('products')
          .update(patch)
          .eq('category', category);
        
        if (error) throw error;
      }
      showToast(`Ventes Flash ${status ? 'activées' : 'désactivées'} pour ${category}`);
    } catch (error) {
      console.error('Error updating category flash sale:', error.message);
      showToast("Erreur de synchronisation", "error");
    }
  }, [setProducts, showToast, view, adminProductsRequest]);

  const addToCart = useCallback((e, product, selectedOptions = null, customPrice = null, extra = {}) => {
    if (e) e.stopPropagation();
    
    const cartItemId = selectedOptions 
      ? `${product.id}-${Object.values(selectedOptions).join('-')}`
      : product.id;

    setCart(prev => {
      const existing = prev.find(item => item.cartItemId === cartItemId);
      if (existing) {
        return prev.map(item => item.cartItemId === cartItemId ? { ...item, quantity: item.quantity + 1 } : item);
      }
      return [...prev, { 
        ...product, 
        cartItemId, 
        selectedOptions, 
        price: customPrice || getEffectivePrice(product),
        quantity: 1,
        onOrder: Boolean(extra.onOrder) || Boolean(product.onOrder)
      }];
    });
    
    // Event GA4
    ReactGA.event({
      category: 'Ecommerce',
      action: 'Add to Cart',
      label: product.label,
      value: Math.round(customPrice || getEffectivePrice(product))
    });

    showToast(`${product.label} ajouté`);
    setIsCartOpen(true);
  }, [showToast, setCart, getEffectivePrice, setIsCartOpen]);

  const updateCartQuantity = (cartItemId, delta) => setCart(prev => prev.map(item => item.cartItemId === cartItemId ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item));
  const removeFromCart = (cartItemId) => {
    setCart(prev => prev.filter(item => item.cartItemId !== cartItemId));
    showToast("Retiré du panier", "info");
  };

  const openProduct = (product) => { 
    setSelectedProduct(product); 
    setView('product-detail'); 
    window.scrollTo(0,0);
    
    // Event GA4
    ReactGA.event({
      category: 'Navigation',
      action: 'View Product',
      label: product.label
    });

    setRecentlyViewed(prev => {
      const filtered = prev.filter(p => p.id !== product.id);
      return [product, ...filtered].slice(0, 4);
    });
  };

  const logoutAdmin = async () => { 
    await supabase.auth.signOut();
    setView('home'); 
    showToast("Session terminée", "info"); 
  };

  const handleLogout = async () => {
    // Best-effort persistence for authenticated users
    if (session?.user?.id && !isGuest) {
      await saveUserStateToSupabase(session.user.id, { favorites, recentlyViewed, buyerInfo });
    }

    await supabase.auth.signOut();
    setIsGuest(false);
    setBuyerInfo({
      name: '',
      firstName: '',
      lastName: '',
      company: '',
      email: '',
      role: '',
      phone: '',
      address: '',
      postalCode: '',
      city: '',
      country: 'France',
      savedAddresses: []
    });
    localStorage.removeItem(`dv_buyer_info_${session?.user?.id || ''}`);
    localStorage.removeItem('dv_buyer_info');
    localStorage.removeItem('dv_is_logged_in');
    setFavorites([]);
    setRecentlyViewed([]);
    setSelectedProduct(null);
    setView('home');
    showToast("Déconnexion réussie", "info");
  };

  const handleGuestLogin = () => {
    setBuyerInfo({
      name: 'Invité',
      firstName: '',
      lastName: '',
      company: 'DEEPVISION HUB',
      email: 'guest@deepvision-hub.eu',
      role: 'Guest',
      phone: '',
      address: '',
      postalCode: '',
      city: '',
      country: 'France',
      savedAddresses: []
    });
    setIsGuest(true);
    setShowLoginModal(false);
    showToast("Connecté en tant qu'invité", "success");
  };

  const toggleCompare = useCallback((e, product) => {
    if (e) e.stopPropagation();
    setCompareList(prev => {
      const exists = prev.find(p => p.id === product.id);
      if (exists) {
        showToast("Retiré du comparateur", "info");
        return prev.filter(p => p.id !== product.id);
      }
      if (prev.length >= 4) {
        showToast("Maximum 4 produits", "error");
        return prev;
      }
      showToast("Ajouté au comparateur");
      return [...prev, product];
    });
  }, [showToast, setCompareList]);

  const [recoveryEmailInput, setRecoveryEmailInput] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const resetRecovery = useCallback(() => {
    setShowRecovery(false);
    setIsVerifyingCode(false);
    setRecoveryCode('');
    setRecoveryExpiry(null);
    setRecoveryEmailInput('');
    setIsResettingPassword(false);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const email = fd.get('user');
    const pass = fd.get('pass');
    
    const { error } = await supabase.auth.signInWithPassword({
      email: email,
      password: pass,
    });

    if (error) {
      showToast("Identifiants incorrects : " + error.message, "error");
    } else {
      showToast("Connexion réussie");
    }
  };

  const handleRecovery = async (e) => {
    if (e) e.preventDefault();
    const email = recoveryEmailInput;
    if (!email) {
      showToast("Veuillez entrer votre email", "error");
      return;
    }

    // Si c'est l'admin (basé sur l'email de récupération configuré)
    if (email.toLowerCase() === adminCredentials.recoveryEmail.toLowerCase()) {
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      const expiry = Date.now() + 60000; // 60 secondes
      
      setIsSendingEmail(true);
      const sent = await sendRecoveryEmail(email, code);
      setIsSendingEmail(false);

      if (sent) {
        setRecoveryCode(code);
        setRecoveryExpiry(expiry);
        setIsVerifyingCode(true);
        showToast(`Code envoyé à l'adresse sécurisée`, "info");
      } else {
        showToast("Erreur lors de l'envoi du mail", "error");
      }
    } else {
      // Si c'est un client (ou admin via Supabase)
      setIsSendingEmail(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin,
      });
      setIsSendingEmail(false);

      if (error) {
        showToast("Erreur : " + error.message, "error");
      } else {
        showToast("Lien de réinitialisation envoyé par email", "success");
        resetRecovery();
      }
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const newPass = fd.get('newPass');
    const confirmPass = fd.get('confirmPass');

    if (newPass !== confirmPass) {
      showToast("Les mots de passe ne correspondent pas", "error");
      return;
    }

    setIsSendingEmail(true);
    const { error } = await supabase.auth.updateUser({ password: newPass });
    setIsSendingEmail(false);

    if (error) {
      showToast("Erreur : " + error.message, "error");
    } else {
      showToast("Mot de passe mis à jour avec succès", "success");
      resetRecovery();
    }
  };

  const verifyRecoveryCode = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const enteredCode = fd.get('code');

    if (Date.now() > recoveryExpiry) {
      showToast("Le code a expiré (60s dépassées)", "error");
      resetRecovery();
      return;
    }

    if (enteredCode === recoveryCode) {
      showToast("Code validé !");
      alert(`IDENTIFIANTS RÉCUPÉRÉS :\nUtilisateur : ${adminCredentials.user}\nMot de passe : ${adminCredentials.pass}`);
      resetRecovery();
    } else {
      showToast("Code incorrect", "error");
    }
  };

  const updateAdminCreds = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const newUser = fd.get('newUser');
    const newPass = fd.get('newPass');
    const newEmail = fd.get('newEmail');

    if (newUser && newPass && newEmail) {
      setAdminCredentials({
        user: newUser,
        pass: newPass,
        recoveryEmail: newEmail
      });
      setIsChangingCreds(false);
      showToast("Identifiants mis à jour");
      try { triggerCredsSaved(); } catch(e) {}
    }
  };

  const saveProduct = async (formData) => {
    try {
      const isAdminView = view === 'admin';
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      // En local, on utilise Supabase directement même en mode admin
      if (isAdminView && !isLocalhost) {
        const mapped = mapProductToDb(formData);
        if (editingProduct) {
          const out = await adminProductsRequest({ action: 'update', id: editingProduct.id, data: mapped });
          const row = Array.isArray(out?.data) ? out.data[0] : null;
          if (!row) throw new Error('Réponse serveur invalide (update)');
          setProducts(prev => prev.map(p => p.id === editingProduct.id ? mapProductFromDb(row) : p));
          setEditingProduct(null);
          showToast('Article mis à jour');
          // Recharger les catégories après la mise à jour
          await fetchAllCategories();
          return;
        }

        const out = await adminProductsRequest({ action: 'insert', data: mapped });
        const row = Array.isArray(out?.data) ? out.data[0] : null;
        if (!row) throw new Error('Réponse serveur invalide (insert)');
        setProducts(prev => [mapProductFromDb(row), ...prev]);
        setIsAddingProduct(false);
        showToast('Nouvel article créé');
        // Recharger les catégories après l'ajout
        await fetchAllCategories();
        return;
      }

      if (editingProduct) {
        const { data, error } = await supabase
          .from('products')
          .update(mapProductToDb(formData))
          .eq('id', editingProduct.id)
          .select();
        
        if (error) throw error;

        if (!data || data.length === 0) {
          throw new Error("Action bloquée par Supabase (RLS). Vérifiez vos politiques UPDATE sur 'products'.");
        }
        
        setProducts(prev => prev.map(p => p.id === editingProduct.id ? mapProductFromDb(data[0]) : p));
        setEditingProduct(null);
        showToast("Article mis à jour");
        // Recharger les catégories après la mise à jour
        await fetchAllCategories();
      } else {
        const { data, error } = await supabase
          .from('products')
          .insert([mapProductToDb(formData)])
          .select();
        
        if (error) throw error;

        if (!data || data.length === 0) {
          throw new Error("Action bloquée par Supabase (RLS). Vérifiez vos politiques INSERT sur 'products'.");
        }
        
        setProducts(prev => [mapProductFromDb(data[0]), ...prev]);
        setIsAddingProduct(false);
        showToast("Nouvel article créé");
        // Recharger les catégories après l'ajout
        await fetchAllCategories();
      }
    } catch (error) {
      const msg = error?.message || 'Problème base de données';
      console.error('Error saving product:', error);
      showToast(`Erreur lors de l'enregistrement : ${msg}`, "error");
    }
  };

  const isUuid = (value) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(String(value || ''));
  };

  const chunkArray = (arr, size) => {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  };

  const normalizeCategory = (value) => String(value || '').trim().toLowerCase();

  const bulkUpdateProductsCategory = useCallback(async (productIds, targetCategory) => {
    const trimmedTarget = String(targetCategory || '').trim();
    if (!trimmedTarget) {
      showToast('Catégorie cible invalide', 'error');
      return { updated: 0 };
    }

    const ids = Array.isArray(productIds) ? productIds : [];
    if (ids.length === 0) return { updated: 0 };

    const dbIds = ids.filter(isUuid);
    const expected = dbIds.length;

    try {
      const isAdminView = view === 'admin';
      if (dbIds.length > 0) {
        if (isAdminView) {
          // Via API server-side (Service Role)
          for (const batch of chunkArray(dbIds, 200)) {
            const out = await adminProductsRequest({ action: 'bulkUpdateCategory', ids: batch, category: trimmedTarget });
            const updated = Number(out?.updated) || (Array.isArray(out?.data) ? out.data.length : 0);
            if (updated === 0 && batch.length > 0) {
              throw new Error('Aucune ligne mise à jour (vérifier config serveur)');
            }
          }
        } else {
          // Direct Supabase (peut être bloqué par RLS)
          for (const batch of chunkArray(dbIds, 100)) {
            const { data, error } = await supabase
              .from('products')
              .update({ category: trimmedTarget })
              .in('id', batch)
              .select('id');

            if (error) throw error;

            // Si le RLS bloque, Supabase renvoie souvent un tableau vide avec select().
            if ((!data || data.length === 0) && batch.length > 0) {
              throw new Error("Action bloquée par Supabase (RLS). Vérifiez vos politiques UPDATE sur 'products'.");
            }
          }
        }
      }

      // Mise à jour locale (inclut aussi les IDs temporaires).
      setProducts(prev => prev.map(p => (ids.includes(p.id) ? { ...p, category: trimmedTarget } : p)));
      setCart(prev => prev.map(p => (ids.includes(p.id) ? { ...p, category: trimmedTarget } : p)));

      return { updated: expected };
    } catch (error) {
      console.error('Error bulk updating category:', error?.message || error);
      showToast(`Erreur : ${error?.message || "mise à jour catégorie"}`, 'error');
      return { updated: 0, error };
    }
  }, [showToast, setProducts, setCart, view, adminProductsRequest]);

  const renameCategory = useCallback(async (fromCategory, toCategory) => {
    const fromNorm = normalizeCategory(fromCategory);
    const toTrim = String(toCategory || '').trim();
    const toNorm = normalizeCategory(toTrim);

    if (!fromNorm) {
      showToast('Catégorie source invalide', 'error');
      return;
    }
    if (!toTrim) {
      showToast('Nouveau nom de catégorie invalide', 'error');
      return;
    }
    if (fromNorm === toNorm) {
      showToast('La catégorie est identique', 'info');
      return;
    }

    const ids = (Array.isArray(products) ? products : [])
      .filter(p => normalizeCategory(p?.category) === fromNorm)
      .map(p => p.id);

    const { updated } = await bulkUpdateProductsCategory(ids, toTrim);
    if (updated > 0) showToast(`Catégorie renommée : ${fromCategory} → ${toTrim}`);
  }, [products, bulkUpdateProductsCategory, showToast]);

  const mergeCategories = useCallback(async (sourceCategories, targetCategory) => {
    const sources = (Array.isArray(sourceCategories) ? sourceCategories : [])
      .map(c => String(c || '').trim())
      .filter(Boolean);
    const targetTrim = String(targetCategory || '').trim();

    if (sources.length === 0) {
      showToast('Sélectionnez au moins une catégorie', 'error');
      return;
    }
    if (!targetTrim) {
      showToast('Catégorie cible invalide', 'error');
      return;
    }

    const targetNorm = normalizeCategory(targetTrim);
    const sourceNorms = new Set(sources.map(normalizeCategory).filter(Boolean));
    sourceNorms.delete(targetNorm);

    if (sourceNorms.size === 0) {
      showToast('Rien à fusionner', 'info');
      return;
    }

    const ids = (Array.isArray(products) ? products : [])
      .filter(p => sourceNorms.has(normalizeCategory(p?.category)))
      .map(p => p.id);

    const { updated } = await bulkUpdateProductsCategory(ids, targetTrim);
    if (updated > 0) showToast(`Catégories fusionnées vers : ${targetTrim}`);
  }, [products, bulkUpdateProductsCategory, showToast]);

  const deleteCategory = useCallback(async (categoryToDelete, fallbackCategory = 'Général') => {
    const fromTrim = String(categoryToDelete || '').trim();
    const fallbackTrim = String(fallbackCategory || 'Général').trim() || 'Général';
    if (!fromTrim) {
      showToast('Catégorie invalide', 'error');
      return;
    }
    if (normalizeCategory(fromTrim) === normalizeCategory(fallbackTrim)) {
      showToast('Catégorie de remplacement invalide', 'error');
      return;
    }
    await mergeCategories([fromTrim], fallbackTrim);
  }, [mergeCategories, showToast]);

  const duplicateProduct = async (product) => {
    try {
      const duplicatedData = {
        ...product,
        label: `${product.label} (Copie)`,
        ref: `${product.ref}-COPY`
      };

      const isAdminView = view === 'admin';
      if (isAdminView) {
        const out = await adminProductsRequest({ action: 'insert', data: mapProductToDb(duplicatedData) });
        const row = Array.isArray(out?.data) ? out.data[0] : null;
        if (!row) throw new Error('Réponse serveur invalide (duplication)');
        setProducts(prev => [mapProductFromDb(row), ...prev]);
        showToast('Article dupliqué');
        return;
      }

      const { data, error } = await supabase
        .from('products')
        .insert([mapProductToDb(duplicatedData)])
        .select();
      
      if (error) throw error;
      
      setProducts(prev => [mapProductFromDb(data[0]), ...prev]);
      showToast("Article dupliqué");
    } catch (error) {
      console.error('Error duplicating product:', error.message);
      showToast("Erreur lors de la duplication", "error");
    }
  };

  const deleteProduct = useCallback(async (id) => {
    try {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (uuidRegex.test(id)) {
        const isAdminView = view === 'admin';
        if (isAdminView) {
          await adminProductsRequest({ action: 'delete', id });
        } else {
          const { data, error } = await supabase
            .from('products')
            .delete()
            .eq('id', id)
            .select();
          
          if (error) throw error;

          // Si data est vide, c'est que le RLS a bloqué la suppression
          if (!data || data.length === 0) {
            throw new Error("Action bloquée par les politiques de sécurité Supabase (RLS). Veuillez autoriser DELETE pour le rôle 'anon'.");
          }
        }
      }

      setProducts(prev => prev.filter(p => p.id !== id));
      setCart(prev => prev.filter(p => p.id !== id));
      setSelectedProductIds(prev => prev.filter(selectedId => selectedId !== id));
      showToast("Article supprimé", "info");
    } catch (error) {
      console.error('Error deleting product:', error.message);
      showToast(`Erreur : ${error.message}`, "error");
    }
  }, [showToast, setProducts, setCart, view, adminProductsRequest]);

  const deleteSelectedProducts = async () => {
    if (selectedProductIds.length === 0) return;
    if (window.confirm(`Supprimer ${selectedProductIds.length} article(s) ?`)) {
      try {
        // Séparer les IDs réels (UUID) des IDs temporaires (imp-...)
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const dbIds = selectedProductIds.filter(id => uuidRegex.test(id));
        
        if (dbIds.length > 0) {
          if (view === 'admin') {
            const result = await adminProductsRequest({ action: 'bulkDelete', ids: dbIds });
            const deleted = Number(result?.deleted ?? (Array.isArray(result?.data) ? result.data.length : 0)) || 0;
            if (deleted === 0) throw new Error("Aucun article supprimé via l'API admin.");
          } else {
            const { data, error } = await supabase
              .from('products')
              .delete()
              .in('id', dbIds)
              .select();
            
            if (error) throw error;

            if (!data || data.length === 0) {
              throw new Error("Action bloquée par Supabase (RLS). Vérifiez vos politiques DELETE.");
            }
          }
        }

        // Mise à jour locale
        setProducts(prev => prev.filter(p => !selectedProductIds.includes(p.id)));
        setCart(prev => prev.filter(p => !selectedProductIds.includes(p.id)));
        showToast(`${selectedProductIds.length} articles supprimés`, "info");
        setSelectedProductIds([]);
      } catch (error) {
        console.error('Error deleting products:', error.message);
        showToast(`Erreur : ${error.message}`, "error");
      }
    }
  };

  const handleUpdateStock = async (productId, delta, variantId = null, isMatrix = false, comboOptions = null) => {
    let updatedProduct = null;
    
    setProducts(prev => {
      const newState = prev.map(p => {
        if (p.id !== productId) return p;

        let newVariants = Array.isArray(p.variants) ? p.variants.map(v => ({ ...v })) : [];

        if (isMatrix && comboOptions) {
          let variantIndex = newVariants.findIndex(v =>
            Object.entries(comboOptions).every(([opt, val]) => v.options[opt] === val)
          );
          if (variantIndex > -1) {
            newVariants[variantIndex] = {
              ...newVariants[variantIndex],
              stock: Math.max(0, delta)
            };
          } else {
            newVariants.push({
              id: `v-${Math.random().toString(36).substr(2, 9)}`,
              options: comboOptions,
              stock: Math.max(0, delta),
              priceMod: 0
            });
          }
        }

        if (variantId && p.variants && !isMatrix) {
          newVariants = p.variants.map(v =>
            v.id === variantId
              ? { ...v, stock: Math.max(0, (v.stock || 0) + delta) }
              : v
          );
        }

        const totalStock = newVariants.length > 0
          ? newVariants.reduce((acc, v) => acc + (v.stock || 0), 0)
          : Math.max(0, (p.stock || 0) + delta);

        updatedProduct = { ...p, variants: newVariants, stock: totalStock };
        return updatedProduct;
      });
      return newState;
    });

    if (updatedProduct) {
      try {
        if (view === 'admin') {
          await adminProductsRequest({
            action: 'update',
            id: productId,
            data: {
              stock: updatedProduct.stock
              // variants: updatedProduct.variants // if you have a variants column
            }
          });
        } else {
          await supabase
            .from('products')
            .update({ 
              stock: updatedProduct.stock, 
              // variants: updatedProduct.variants // if you have a variants column
            })
            .eq('id', productId);
        }
      } catch (error) {
        console.error('Error updating stock in Supabase:', error.message);
      }
    }
  };

  const handleBulkStockUpdate = async (amount) => {
    if (selectedProductIds.length === 0) return;
    
    const updatedProducts = [];
    setProducts(prev => {
      const newState = prev.map(p => {
        if (!selectedProductIds.includes(p.id)) return p;

        const updatedProduct = { ...p, stock: Math.max(0, (p.stock || 0) + amount) };

        if (updatedProduct.variants && updatedProduct.variants.length > 0) {
          updatedProduct.variants = updatedProduct.variants.map(v => ({
            ...v,
            stock: Math.max(0, (v.stock || 0) + amount)
          }));
          updatedProduct.stock = updatedProduct.variants.reduce((acc, v) => acc + (v.stock || 0), 0);
        }

        updatedProducts.push(updatedProduct);
        return updatedProduct;
      });
      return newState;
    });

    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    for (const p of updatedProducts) {
      if (!uuidRegex.test(p.id)) continue;
      try {
        if (view === 'admin') {
          await adminProductsRequest({ action: 'update', id: p.id, data: { stock: p.stock } });
        } else {
          await supabase
            .from('products')
            .update({ stock: p.stock })
            .eq('id', p.id);
        }
      } catch (error) {
        console.error(`Error bulk updating stock for product ${p.id}:`, error.message);
      }
    }
    
    showToast(`Stock mis à jour (+${amount}) pour ${selectedProductIds.length} articles`);
  };

  const handleGranularBulkStockUpdate = async (selections, amount) => {
    const updatedProducts = [];
    setProducts(prev => {
      const newState = prev.map(p => {
        let updatedProduct = { ...p };
        let changed = false;
        
        // Check if the product itself is selected (global stock)
        const productSelection = selections.find(s => s.productId === p.id && !s.variantId);
        if (productSelection) {
          updatedProduct.stock = Math.max(0, (updatedProduct.stock || 0) + amount);
          changed = true;
        }

        // Check if any variants are selected
        if (updatedProduct.variants && updatedProduct.variants.length > 0) {
          let variantsChanged = false;
          updatedProduct.variants = updatedProduct.variants.map(v => {
            const variantSelection = selections.find(s => 
              s.productId === p.id && s.variantId === v.id
            );
            if (variantSelection) {
              variantsChanged = true;
              return { ...v, stock: Math.max(0, (v.stock || 0) + amount) };
            }
            return v;
          });
          if (variantsChanged) {
            updatedProduct.stock = updatedProduct.variants.reduce((acc, v) => acc + (v.stock || 0), 0);
            changed = true;
          }
        }

        if (changed) updatedProducts.push(updatedProduct);
        return updatedProduct;
      });
      return newState;
    });

    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    for (const p of updatedProducts) {
      if (!uuidRegex.test(p.id)) continue;
      try {
        if (view === 'admin') {
          await adminProductsRequest({ action: 'update', id: p.id, data: { stock: p.stock } });
        } else {
          await supabase
            .from('products')
            .update({ stock: p.stock })
            .eq('id', p.id);
        }
      } catch (error) {
        console.error(`Error bulk updating stock for product ${p.id}:`, error.message);
      }
    }

    showToast(`Stock mis à jour (+${amount}) pour ${selections.length} éléments sélectionnés`);
  };

  const decrementStock = async (items) => {
    const productsToUpdate = [];
    
    // 1. Calculer les nouveaux stocks localement
    setProducts(prev => {
      return prev.map(p => {
        const itemsForThisProduct = items.filter(item => item.id === p.id);
        if (itemsForThisProduct.length === 0) return p;

        let updatedProduct = { ...p };

        itemsForThisProduct.forEach(cartItem => {
          // Décrémenter le stock global
          updatedProduct.stock = Math.max(0, (updatedProduct.stock || 0) - cartItem.quantity);

          // Décrémenter le stock des variantes (combinaisons)
          if (cartItem.selectedOptions && updatedProduct.variants) {
            updatedProduct.variants = updatedProduct.variants.map(v => {
              const isMatch = Object.entries(cartItem.selectedOptions).every(
                ([optName, valLabel]) => v.options[optName] === valLabel
              );
              if (isMatch) {
                return { ...v, stock: Math.max(0, (v.stock || 0) - cartItem.quantity) };
              }
              return v;
            });
          }
          
          // Décrémenter le stock_map (pour les matrices)
          if (cartItem.selectedOptions && updatedProduct.isMatrix && updatedProduct.stockMap) {
            const key = Object.values(cartItem.selectedOptions).join('|');
            if (updatedProduct.stockMap[key] !== undefined) {
              updatedProduct.stockMap[key] = Math.max(0, updatedProduct.stockMap[key] - cartItem.quantity);
            }
          }
        });

        productsToUpdate.push(updatedProduct);
        return updatedProduct;
      });
    });

    // Note: La synchronisation avec Supabase est maintenant gérée par la fonction RPC 'decrement_stock'
    // appelée dans createSupabaseOrder pour garantir l'atomicité et la sécurité.
  };

  const toggleProductSelection = (id) => {
    setSelectedProductIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleAllSelection = () => {
    if (selectedProductIds.length === products.length) {
      setSelectedProductIds([]);
    } else {
      setSelectedProductIds(products.map(p => p.id));
    }
  };

  const createSupabaseOrder = async (orderData, items) => {
    console.log("Starting createSupabaseOrder with:", { orderData, itemsCount: items?.length });
    try {
      // 0. Check if order already exists (idempotency)
      try {
        const { data: existingOrder } = await supabase
          .from('orders')
          .select('id')
          .eq('ref', orderData.id)
          .maybeSingle();
        
        if (existingOrder) {
          console.log("Order already exists with ID:", existingOrder.id);
          return existingOrder;
        }
      } catch (e) {
        console.warn("Error checking existing order:", e);
      }

      // 1. Get or create client in CRM (clients table) - REQUIRED for foreign key
      let crmClientId = null;
      if (orderData.buyer?.email) {
        console.log("Upserting CRM client:", orderData.buyer.email);
        
        // Utiliser upsert pour créer ou mettre à jour automatiquement
        const { data: upsertedClient, error: upsertError } = await supabase
          .from('clients')
          .upsert({
            email: orderData.buyer.email, // Clé unique pour l'upsert
            name: orderData.buyer.name || '',
            first_name: orderData.buyer.firstName || orderData.buyer.name?.split(' ')[0] || '',
            last_name: orderData.buyer.lastName || orderData.buyer.name?.split(' ').slice(1).join(' ') || '',
            company: orderData.buyer.company || '',
            phone: orderData.buyer.phone || '',
            address: orderData.buyer.address || '',
            postal_code: orderData.buyer.postalCode || '',
            city: orderData.buyer.city || '',
            country: orderData.buyer.country || 'France',
            role: orderData.buyer.role || 'Client',
            last_seen: new Date().toISOString()
          }, {
            onConflict: 'email', // Utiliser l'email comme clé de conflit
            ignoreDuplicates: false // Mettre à jour si existe
          })
          .select()
          .single();
        
        if (upsertError) {
          console.error("CRITICAL: Cannot upsert client:", upsertError);
          throw new Error(`Impossible de créer/mettre à jour le client: ${upsertError.message}`);
        }
        
        if (upsertedClient) {
          crmClientId = upsertedClient.id;
          console.log("Upserted CRM client:", crmClientId);
        } else {
          throw new Error("Client upsert returned no data");
        }
      }

      if (!crmClientId) {
        throw new Error("Impossible de créer ou récupérer le client. Commande annulée.");
      }

      // 2. Create order
      // Use user.id (auth UUID) if logged in, otherwise use crmClientId
      const orderToInsert = {
        ref: orderData.id,
        client_id: crmClientId,  // Toujours défini maintenant
        user_id: user?.id || null,
        total_ht: orderData.totalHT,
        shipping_fee: orderData.shipping,
        shipping_type: orderData.shipping === shippingPrices.express ? 'express' : (orderData.shipping === 0 ? 'pickup' : 'standard'),
        buyer_info: orderData.buyer,
        status: 'received',
        created_at: new Date().toISOString()
      };

      console.log("Inserting order:", orderToInsert);
      let order = null;
      let orderError = null;

      // Try with user_id (new schema). If the column doesn't exist, retry without it.
      ({ data: order, error: orderError } = await supabase
        .from('orders')
        .insert([orderToInsert])
        .select());

      if (orderError && (orderError.code === '42703' || String(orderError.message || '').toLowerCase().includes('user_id'))) {
        const { user_id, ...legacyOrderToInsert } = orderToInsert;
        ({ data: order, error: orderError } = await supabase
          .from('orders')
          .insert([legacyOrderToInsert])
          .select());
      }

      if (orderError) {
        console.error("Order Insert Error:", orderError);
        // If it's an RLS error, we provide a more helpful message
        if (orderError.code === '42501') {
          throw new Error("Permission refusée : La base de données bloque l'enregistrement de cette commande. Vérifiez les politiques RLS sur la table 'orders'.");
        }
        throw new Error(`Erreur base de données : ${orderError.message}`);
      }

      let createdOrder = order && order[0];
      if (!createdOrder) {
        console.log("Order insert didn't return data, fetching by ref...");
        const { data: fetchedOrder } = await supabase
          .from('orders')
          .select('id')
          .eq('ref', orderData.id)
          .maybeSingle();
        
        if (!fetchedOrder) {
          console.error("Could not find order after insert");
          throw new Error("Impossible de récupérer l'ID de la commande créée (RLS ?)");
        }
        createdOrder = { id: fetchedOrder.id };
      }
      console.log("Order created successfully with DB ID:", createdOrder.id);

      // 3. Create order items
      const orderItems = items.map(item => ({
        order_id: createdOrder.id,
        product_id: (item.id && String(item.id).length > 20) ? item.id : null,
        label: item.label || item.name,
        ref: item.ref || '',
        price: item.price,
        quantity: item.quantity,
        selected_options: item.selectedOptions || {},
        reminder_eligible: item.reminderEligible || false
      }));

      console.log("Inserting order items:", orderItems.length);
      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems);

      if (itemsError) {
        console.error("Items Insert Error:", itemsError);
        throw new Error(`Erreur insertion articles: ${itemsError.message}`);
      }

      // 4. Appeler la fonction RPC pour décrémenter les stocks en base
      // Note: Fonction RPC désactivée - le stock est géré localement via decrementStock()
      // Si besoin de synchronisation Supabase, créer la fonction RPC 'decrement_stock' en SQL
      console.log("Stock management: handled locally by decrementStock()");
      
      /*
      const { error: rpcError } = await supabase.rpc('decrement_stock', { 
        order_id: createdOrder.id 
      });

      if (rpcError) {
        console.error("Erreur RPC Stock:", rpcError.message);
        showToast("Commande enregistrée, mais erreur de mise à jour des stocks", "warning");
      }
      */

      // Refresh data to show new client/order in admin
      await fetchData(true);

      console.log("createSupabaseOrder completed successfully");
      return createdOrder;
    } catch (error) {
      console.error('Error creating order in Supabase:', error);
      throw error;
    }
  };

  const generatePDF = async (providedRef, overrideBuyerInfo, itemsOverride = null, options = { createOrder: true }) => {
    const info = overrideBuyerInfo || buyerInfo;
    
    console.log("Generating PDF for:", providedRef, "with info:", info);

    if (!window.jspdf) {
      showToast("Bibliothèque PDF en cours de chargement...", "info");
      console.error("jsPDF not found on window");
      return;
    }

    if (!info || !info.name || !info.company) {
      showToast("Coordonnées incomplètes (Nom et Société requis)", "error");
      console.error("Incomplete buyer info:", info);
      return;
    }

    const { jsPDF } = window.jspdf;
    const orderRef = providedRef || `DV-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
    const doc = new jsPDF();
    const now = new Date();
    const nowStr = now.toLocaleString('fr-FR');

    // Helper pour jsPDF qui ne supporte pas les espaces insécables
    const formatPricePDF = (price) => formatPrice(price).replace(/\s/g, ' ');

    // Header
    doc.setFillColor(30, 41, 59);
    doc.rect(0, 0, 210, 44, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('DEEPVISION HUB', 20, 28);
    doc.setFontSize(10);
    doc.text('BON DE COMMANDE', 150, 28);

    // Client Info Section
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(12);
    doc.text('COORDONNÉES CLIENT', 20, 54);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    // Right side info (Reference, Date, Reminder) - Right aligned to avoid overflow
    doc.text(`Référence : ${orderRef}`, 190, 64, { align: 'right' });
    doc.text(`Date : ${nowStr}`, 190, 72, { align: 'right' });

    if (options.reminder) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(37, 99, 235); // Blue-600
      doc.text(`RAPPEL DE RENOUVELLEMENT : ${new Date(options.reminder.date).toLocaleDateString('fr-FR')}`, 190, 80, { align: 'right' });
      doc.setTextColor(30, 41, 59);
      doc.setFont('helvetica', 'normal');
    }

    // Left side info (Client details) - Wrapped to avoid overflow
    const leftColWidth = 110;
    let currentY = 64;
    
    const addClientLine = (label, value) => {
      const text = `${label} : ${value || 'N/A'}`;
      const lines = doc.splitTextToSize(text, leftColWidth);
      doc.text(lines, 20, currentY);
      currentY += (lines.length * 7);
    };

    addClientLine('Nom', info.name);
    addClientLine('Société', info.company);
    addClientLine('Fonction', info.role);
    addClientLine('Téléphone', info.phone);
    
    // Section Facturation
    doc.setFont('helvetica', 'bold');
    doc.text('Adresse de facturation:', 20, currentY);
    currentY += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(info.address || '', 20, currentY);
    currentY += 5;
    if (info.postalCode || info.city) {
      doc.text(`${info.postalCode || ''} ${info.city || ''}`, 20, currentY);
      currentY += 5;
    }
    if (info.country) {
      doc.text(info.country, 20, currentY);
      currentY += 5;
    }
    
    // Section Livraison si différente
    if (info.deliveryAddress || info.shippingAddress) {
      currentY += 5;
      doc.setFont('helvetica', 'bold');
      doc.text('Adresse de livraison :', 20, currentY);
      currentY += 7;
      doc.setFont('helvetica', 'normal');
      addClientLine('', info.deliveryAddress || info.shippingAddress);
    }
    
    // Instructions de livraison (affichées même si adresse identique)
    if (info.deliveryInstructions) {
      if (!info.deliveryAddress && !info.shippingAddress) {
        currentY += 5;
      }
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      
      // Ajouter l'icône de livraison à gauche du texte
      try {
        const deliveryIcon = await fetch('/images/delivery-icon.png');
        const iconBlob = await deliveryIcon.blob();
        const iconBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(iconBlob);
        });
        // Icône alignée avec le texte (10x10mm)
        doc.addImage(iconBase64, 'PNG', 20, currentY - 4, 10, 10);
        doc.text('Informations pour le livreur:', 32, currentY + 1);
      } catch (error) {
        console.log('Erreur chargement icône livraison:', error);
        doc.text('Informations pour le livreur:', 20, currentY);
      }
      
      currentY += 7;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      const instructionLines = doc.splitTextToSize(info.deliveryInstructions, 160);
      doc.text(instructionLines, 20, currentY);
      currentY += (instructionLines.length * 5);
      doc.setFontSize(10);
      doc.setTextColor(30, 41, 59);
    }
    
    currentY += 5;
    addClientLine('Email', info.email);

    // Prepare items (may come from snapshot)
    const items = Array.isArray(itemsOverride) ? itemsOverride : (Array.isArray(cart) ? cart : []);

    // If client accepted sur-commande delays, render a visible remark box
    const hasSurCommande = items.some(it => Boolean(it && it.onOrder) || (it && typeof it.stock === 'number' && it.stock <= 0));
    let headerY = Math.max(currentY + 10, 140); 
    if (hasSurCommande) {
      // Draw compact blue acceptance/info box with rounded corners
      const title = 'Articles sur commande';
      const para = (options && options.acceptedOnOrder)
        ? 'Confirmation : le client a accepté ces conditions de vente et les délais de livraison rallongés pour les articles sur commande.'
        : 'Rappel : les délais de livraison pour ces articles seront plus longs et ne sont pas garantis.';
      const maxWidth = 140;
      const paraLines = doc.splitTextToSize(para, maxWidth);
      const lineHeight = 5.5;
      const boxPadding = 6;
      let boxHeight = (paraLines.length * lineHeight) + boxPadding * 2 + 8;
      
      if (options && options.acceptedOnOrder) {
        boxHeight += 10; // Augmenter la hauteur pour inclure la ligne d'acceptation
      }

      const boxX = 20;
      const boxY = currentY + 5;
      const boxWidth = 150;

      // Rounded rectangle (use roundedRect if available, fallback to rect)
      try {
        if (typeof doc.roundedRect === 'function') {
          doc.setFillColor(29, 78, 216);
          doc.setDrawColor(24, 64, 160);
          doc.roundedRect(boxX, boxY, boxWidth, boxHeight, 4, 4, 'F');
        } else {
          doc.setFillColor(29, 78, 216);
          doc.setDrawColor(24, 64, 160);
          doc.rect(boxX, boxY, boxWidth, boxHeight, 'F');
        }
      } catch (e) {
        doc.setFillColor(29, 78, 216);
        doc.setDrawColor(24, 64, 160);
        doc.rect(boxX, boxY, boxWidth, boxHeight, 'F');
      }

      // Title (white, smaller)
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);
      doc.text(title, boxX + boxPadding, boxY + boxPadding + 3);

      // Paragraph (lighter white, smaller)
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(235, 245, 255);
      const paraX = boxX + boxPadding;
      const paraY = boxY + boxPadding + 10;
      doc.text(paraLines, paraX, paraY);

      // If accepted, add accepted by line with name and date (smaller)
      if (options && options.acceptedOnOrder) {
        const acceptedBy = `Accepté par : ${info.name || ''} — ${nowStr}`;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(235, 245, 255);
        doc.text(acceptedBy, paraX, paraY + paraLines.length * lineHeight + 6);
        headerY = boxY + boxHeight + 12;
      } else {
        headerY = boxY + boxHeight + 10;
      }

      // restore default text color
      doc.setTextColor(30, 41, 59);
    }

    // Table Header
    doc.setDrawColor(226, 232, 240);
    doc.line(20, headerY - 10, 190, headerY - 10);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('DÉSIGNATION', 20, headerY);
    doc.text('QTÉ', 160, headerY, { align: 'center' });
    doc.text('TOTAL HT', 190, headerY, { align: 'right' });
    doc.line(20, headerY + 4, 190, headerY + 4);

    // Items
    let y = headerY + 12;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    items.forEach(item => {
      const label = item && item.label ? String(item.label) : 'Article';
      const designationWidth = 100; // mm
      const labelLines = doc.splitTextToSize(label, designationWidth);
      doc.setTextColor(30, 41, 59);
      doc.text(labelLines, 20, y);

      // quantity centered in its column
      const qtyY = y + (labelLines.length - 1) * 6;
      doc.text(`${item.quantity || 0}`, 160, qtyY, { align: 'center' });

      // item total right aligned
      const itemTotal = formatPricePDF((item.price || 0) * (item.quantity || 0));
      doc.text(itemTotal, 190, qtyY, { align: 'right' });

      // If the item has selected options, print them under designation
      if (item && item.selectedOptions && Object.keys(item.selectedOptions).length > 0) {
        const opts = Object.entries(item.selectedOptions).map(([k, v]) => `${k}: ${v}`).join(' • ');
        const maxWidth = designationWidth - 4;
        const optLines = doc.splitTextToSize(opts, maxWidth);
        y += (labelLines.length * 6);
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(optLines, 22, y + 4);
        doc.setFontSize(10);
        doc.setTextColor(30, 41, 59);
        y += (optLines.length * 6) + 6;
      } else {
        y += (labelLines.length * 6) + 6;
      }

      // Mark sur-commande if applicable
      const isSurCommande = Boolean(item.onOrder) || (typeof item.stock === 'number' && item.stock <= 0);
      if (isSurCommande) {
        const badge = 'Sur commande';
        doc.setFontSize(8);
        doc.setTextColor(160, 110, 30);
        doc.text(badge, 22, y - 4);
        doc.setFontSize(10);
        doc.setTextColor(30, 41, 59);
        y += 6;
      }

      // Page break if needed
      if (y > 260) {
        doc.addPage();
        y = 20;
      }
    });

    // Summary (incl. frais de livraison)
    y += 6;
    doc.line(120, y, 190, y);
    y += 8;
    const transport = Number(shippingFee || 0);
    const cartTotalLocal = items.reduce((s, it) => s + ((it.price || 0) * (it.quantity || 0)), 0);
    const subtotalHT = cartTotalLocal + transport;
    const tva = subtotalHT * 0.2;
    const ttc = subtotalHT + tva;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('TOTAL ARTICLES HT :', 120, y);
    doc.text(formatPricePDF(cartTotalLocal), 190, y, { align: 'right' });

    y += 8;
    doc.text('Transport HT :', 120, y);
    if (transport === 0) {
      doc.text('Retrait sur place', 190, y, { align: 'right' });
    } else {
      doc.text(formatPricePDF(transport), 190, y, { align: 'right' });
    }

    y += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('SOUS-TOTAL HT :', 120, y);
    doc.text(formatPricePDF(subtotalHT), 190, y, { align: 'right' });

    y += 8;
    doc.setFont('helvetica', 'normal');
    doc.text('TVA (20%) :', 120, y);
    doc.text(formatPricePDF(tva), 190, y, { align: 'right' });

    y += 10;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL TTC :', 120, y);
    doc.text(formatPricePDF(ttc), 190, y, { align: 'right' });

    if (options.createOrder) {
      const newOrder = { 
        id: orderRef, 
        date: now.toLocaleDateString(), 
        buyer: { ...info }, 
        totalHT: subtotalHT, 
        totalTTC: ttc, 
        itemsCount: items.length, 
        items: [...items],
        shipping: transport,
        shippingLabel: transport === 0 ? 'Retrait sur place' : formatPrice(transport),
        acceptedOnOrder: Boolean(options.acceptedOnOrder),
        reminder: options.reminder || null,
        type: 'PDF' 
      };

      // Save to Supabase
      try {
        await createSupabaseOrder(newOrder, items);
      } catch (err) {
        console.error("Erreur lors de la création de la commande PDF:", err);
        showToast(`Erreur lors de l'enregistrement de la commande : ${err.message || 'Problème base de données'}`, "error");
        // On continue quand même pour le PDF car c'est une génération locale
      }

      setOrders(prev => {
        const safePrev = Array.isArray(prev) ? prev : [];
        if (safePrev.some(o => o.id === orderRef)) return safePrev;
        const updated = [newOrder, ...safePrev];
        return updated;
      });

      decrementStock(items);

      // Event GA4
      ReactGA.event({
        category: 'Conversion',
        action: 'Generate PDF',
        value: Math.round(ttc)
      });
    }

    // Générer le PDF en base64 pour l'email
    const pdfBase64 = doc.output('datauristring').split(',')[1];
    
    // Télécharger aussi le PDF localement
    doc.save(`Devis_${orderRef}.pdf`);
    
    return pdfBase64;
  };

  if (typeof window !== 'undefined') window.generatePDF = generatePDF;

  const sendOrderEmail = async (providedRef, overrideBuyerInfo, acceptedOnOrder = false, reminderInfo = null) => {
    const info = overrideBuyerInfo || buyerInfo;
    if (!info.name || !info.company || !info.email) {
      showToast("Informations client requises", "error");
      return;
    }
    setIsSendingEmail(true);
    const orderRef = providedRef || `DV-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
    const transport = Number(shippingFee || 0);

    try {
      const subtotalHT = cartTotal + transport;
      const totalTTC = subtotalHT * 1.2;

      const newOrder = { 
        id: orderRef, 
        date: new Date().toLocaleDateString(), 
        buyer: { ...info }, 
        totalHT: subtotalHT, 
        totalTTC: totalTTC, 
        itemsCount: cartCount, 
        items: [...cart],
        shipping: transport,
        shippingLabel: transport === 0 ? 'Retrait sur place' : formatPrice(transport),
        acceptedOnOrder: Boolean(acceptedOnOrder),
        reminder: reminderInfo,
        type: 'EMAIL' 
      };

      // 1. Sauvegarde Supabase en priorité
      try {
        await createSupabaseOrder(newOrder, cart);
        
        setOrders(prev => {
          const safePrev = Array.isArray(prev) ? prev : [];
          if (safePrev.some(o => o.id === orderRef)) return safePrev;
          return [newOrder, ...safePrev];
        });
      } catch (dbErr) {
        console.error("Erreur Supabase:", dbErr);
        showToast(`Erreur lors de l'enregistrement de la commande : ${dbErr.message || 'Problème base de données'}`, "error");
        // On arrête tout si la sauvegarde DB échoue
        setIsSendingEmail(false);
        return;
      }

      // 2. Envoi de l'email
      {
        const itemsTable = cart.map(item => `
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #e2e8f0;">${item.label || item.name}</td>
            <td style="padding: 8px; border-bottom: 1px solid #e2e8f0; text-align: center;">${item.quantity}</td>
            <td style="padding: 8px; border-bottom: 1px solid #e2e8f0; text-align: right;">${formatPrice(item.price)}</td>
          </tr>
        `).join('');

        try {
          // Générer le PDF pour la pièce jointe
          const pdfBase64 = await generatePDF(orderRef, info, cart, { createOrder: false });

          // URL de l'API email (localhost en dev, VPS Hostinger en prod via /send-email.php)
          const emailApiUrl = getEmailApiUrl();

          // Appel au serveur SMTP (Hostinger)
          const resp = await fetch(emailApiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                from: 'DeepVision Hub <info@deepvision-hub.eu>',
                to: [info.email],
                bcc: Array.from(new Set([
                  'info@deepvision-hub.eu',
                  adminCredentials.recoveryEmail
                ].filter(Boolean))),
                subject: `Merci pour votre commande ${orderRef} - ${info.company}`,
                pdfAttachment: pdfBase64,
                pdfFilename: `Devis_${orderRef}.pdf`,
                html: `
                <div style="font-family: sans-serif; padding: 20px; color: #1e293b;">
                  <h2 style="color: #2563eb;">Confirmation de commande</h2>
                  <p>Bonjour ${info.name},</p>
                  <p>Nous avons bien reçu votre demande de devis pour la société <strong>${info.company}</strong>.</p>
                  <p>Référence : <strong>${orderRef}</strong></p>
                  
                  <h3 style="margin-top: 20px;">Détails de la commande :</h3>
                  <table style="width: 100%; border-collapse: collapse;">
                    <thead>
                      <tr style="background: #f8fafc;">
                        <th style="padding: 8px; text-align: left; border-bottom: 2px solid #e2e8f0;">Article</th>
                        <th style="padding: 8px; text-align: center; border-bottom: 2px solid #e2e8f0;">Qté</th>
                        <th style="padding: 8px; text-align: right; border-bottom: 2px solid #e2e8f0;">Prix HT</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${itemsTable}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colspan="2" style="padding: 8px; text-align: right; font-weight: bold; border-top: 2px solid #e2e8f0;">Sous-total HT</td>
                        <td style="padding: 8px; text-align: right; font-weight: bold; border-top: 2px solid #e2e8f0;">${formatPrice(cartTotal)}</td>
                      </tr>
                      <tr>
                        <td colspan="2" style="padding: 8px; text-align: right;">Transport</td>
                        <td style="padding: 8px; text-align: right;">${transport === 0 ? 'Retrait sur place' : formatPrice(transport)}</td>
                      </tr>
                      <tr style="color: #2563eb; font-size: 1.1em;">
                        <td colspan="2" style="padding: 8px; text-align: right; font-weight: bold;">Total HT</td>
                        <td style="padding: 8px; text-align: right; font-weight: bold;">${formatPrice(cartTotal + transport)}</td>
                      </tr>
                    </tfoot>
                  </table>

                  ${acceptedOnOrder ? `<p style="color: #b45309; background: #fffbeb; padding: 10px; border-radius: 8px; margin-top: 20px;">⚠️ Le client a accepté les délais de livraison rallongés pour les articles sur commande.</p>` : ''}
                  ${reminderInfo ? `<p style="color: #2563eb; background: #eff6ff; padding: 10px; border-radius: 8px;">🔔 Un rappel de renouvellement est programmé pour le : <strong>${new Date(reminderInfo.date).toLocaleDateString('fr-FR')}</strong></p>` : ''}
                  
                  <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 20px 0;" />
                  <p>Notre équipe commerciale reviendra vers vous dans les plus brefs délais.</p>
                </div>
              `
              })
          });

          let responseJson = null;
          try {
            // L'API d'email peut répondre en JSON (succès/erreur)
            responseJson = await resp.clone().json();
          } catch (e) {
            // Ignore JSON parse errors; we'll fall back to status/text.
          }

          if (!resp.ok || (responseJson && responseJson.error)) {
            let hint = '';
            if (resp.status === 405) hint = "Endpoint email: POST non autorisé (405)";
            else if (resp.status === 404) hint = "Endpoint email introuvable (404)";
            else hint = `Endpoint email: HTTP ${resp.status}`;

            const errText =
              (responseJson && (responseJson.error?.message || responseJson.error)) ||
              `${resp.status} ${resp.statusText}`;
            console.error("Email error:", errText);

            const diag = await diagnoseEmailApi(emailApiUrl);
            const extra = diag ? ` — ${diag}` : '';
            showToast(
              `Commande enregistrée, mais l'email n'a pas pu être envoyé (${hint}${extra})`,
              "warning"
            );
          } else {
            // Even if delivery can still bounce later, this confirms the API accepted the request.
            showToast("E-mail de confirmation envoyé.", "success");
          }
        } catch (emailErr) {
          // Ne bloque jamais la confirmation si l'API email est indisponible (ex: "Failed to fetch" en local)
          console.error('Email send failed:', emailErr);

          const emailApiUrl = getEmailApiUrl();
          const diag = await diagnoseEmailApi(emailApiUrl);
          const extra = diag ? ` — ${diag}` : '';
          showToast(`Commande enregistrée, mais l'email n'a pas pu être envoyé (API indisponible${extra})`, "warning");
        }
      }
      
      decrementStock(cart);
      setCart([]);
      // Store context for success screen and show it
      const now = new Date();
      const dateStr = now.toLocaleString('fr-FR');
      const buyerDisplayName =
        (info?.name || '').trim() ||
        `${(info?.firstName || '').trim()} ${(info?.lastName || '').trim()}`.trim();
      setLastOrderRef(orderRef);
      setLastBuyerName(buyerDisplayName);
      setLastBuyerEmail(info.email);
      setLastOrderDate(dateStr);
      setLastOrderShippingLabel(transport === 0 ? 'Retrait sur place' : formatPrice(transport));
      setLastOrderTotal(formatPrice(totalTTC));
      showToast("Commande validée !", "success");
      setView('success');

      // Event GA4
      ReactGA.event({
        category: 'Conversion',
        action: 'Order Sent',
        value: Math.round(totalTTC)
      });

    } catch (err) {
      console.error("Global order error:", err);
      showToast(err.message || "Erreur lors de la commande", "error");
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleExportCSVDetailed = () => {
    if (!window.Papa) return;
    
    const expandedData = [];
    
    products.forEach(p => {
      // Produits matricés : générer les variantes depuis options + stockMap
      if (p.isMatrix && p.options && p.options.length > 0) {
        // Vérifier si c'est un produit avec des options mutuellement exclusives
        // (chaque option n'a qu'une seule valeur = alternatives, pas combinaisons)
        const hasOnlyOneValuePerOption = p.options.every(opt => opt.values && opt.values.length === 1);
        
        if (hasOnlyOneValuePerOption) {
          // Cas spécial : options alternatives (ex: Connecteur femelle OU mâle)
          // Créer une ligne par option au lieu du produit cartésien
          p.options.forEach((opt, idx) => {
            const value = opt.values[0];
            const variantOptions = `${opt.name}: ${value.label}`;
            
            // Créer une référence unique pour chaque variante
            const variantRef = `${p.ref}-${value.label}`.toUpperCase().replace(/\s+/g, '-');
            
            expandedData.push({
              ref: variantRef,
              label: p.label,
              brand: p.brand,
              category: p.category,
              type_article: 'VARIANTE',
              variante_numero: idx + 1,
              variante_options: variantOptions,
              prix_achat_ht: value.purchasePrice || p.purchasePrice || 0,
              marge: value.margin || p.margin || 1.3,
              prix_vente_ht: value.priceMod || p.price || 0,
              stock: p.stockMap?.[value.label] || 0,
              summary: p.summary || '',
              warranty: p.warranty || '',
              weight: p.weight || 0,
              suppliers: p.suppliers && p.suppliers.length > 0 ? p.suppliers[0].name : ''
            });
          });
        } else {
          // Cas normal : produit cartésien des options
          const cartesian = (arrays) => {
            return arrays.reduce((acc, arr) => {
              return acc.flatMap(x => arr.map(y => [...x, y]));
            }, [[]]);
          };
          
          const dimValues = p.options.map(opt => 
            opt.values.map(v => ({ dim: opt.name, value: v.label }))
          );
          
          const combinations = cartesian(dimValues);
          
            combinations.forEach((combo, idx) => {
            const comboOptions = {};
            combo.forEach(item => comboOptions[item.dim] = item.value);
            
            // Clé pour stockMap
            const key = p.options.map(opt => comboOptions[opt.name]).join('|');
            const stock = p.stockMap?.[key] || 0;
            
            // Chercher la variante correspondante si elle existe
            const variant = p.variants?.find(v => 
              Object.entries(comboOptions).every(([optName, valLabel]) => v.options?.[optName] === valLabel)
            );
            
            // Récupérer les prix depuis les valeurs des options
            let prixAchatHT = variant?.purchasePrice || p.purchasePrice || 0;
            let marge = variant?.margin || p.margin || 1.3;
            let prixVenteHT = variant?.priceMod || p.price || 0;
            
            // Si pas de variant, chercher dans les valeurs des options
            p.options.forEach(opt => {
              const selectedValue = comboOptions[opt.name];
              if (selectedValue) {
                const valObj = opt.values.find(v => v.label === selectedValue);
                if (valObj) {
                  // Prendre les valeurs de l'option si elles existent
                  if (valObj.purchasePrice !== undefined && valObj.purchasePrice > 0) {
                    prixAchatHT = valObj.purchasePrice;
                  }
                  if (valObj.margin !== undefined && valObj.margin > 0) {
                    marge = valObj.margin;
                  }
                  if (valObj.priceMod !== undefined && valObj.priceMod > 0) {
                    prixVenteHT = valObj.priceMod;
                  }
                }
              }
            });
            
            const variantOptions = Object.entries(comboOptions)
              .map(([k, v]) => `${k}: ${v}`)
              .join(' | ');
            
            // Créer une référence unique pour chaque combinaison
            const variantRef = `${p.ref}-${key.replace(/\|/g, '-')}`.toUpperCase().replace(/\s+/g, '-');
            
            expandedData.push({
              ref: variantRef,
              label: p.label,
              brand: p.brand,
              category: p.category,
              type_article: 'VARIANTE',
              variante_numero: idx + 1,
              variante_options: variantOptions,
              prix_achat_ht: prixAchatHT,
              marge: marge,
              prix_vente_ht: prixVenteHT,
              stock: stock,
              summary: p.summary || '',
              warranty: p.warranty || '',
              weight: p.weight || 0,
              suppliers: p.suppliers && p.suppliers.length > 0 ? p.suppliers[0].name : ''
            });
          });
        }
      } else if (p.variants && Array.isArray(p.variants) && p.variants.length > 0) {
        // Produits avec variants (ancien système)
        p.variants.forEach((variant, idx) => {
          const variantOptions = Object.entries(variant.options || {})
            .map(([k, v]) => `${k}: ${v}`)
            .join(' | ');
          
          // Créer une référence unique basée sur les options de la variante
          const variantKey = Object.values(variant.options || {}).join('-');
          const variantRef = `${p.ref}-${variantKey}`.toUpperCase().replace(/\s+/g, '-');
          
          expandedData.push({
            ref: variantRef,
            label: p.label,
            brand: p.brand,
            category: p.category,
            type_article: 'VARIANTE',
            variante_numero: idx + 1,
            variante_options: variantOptions,
            prix_achat_ht: variant.purchasePrice || 0,
            marge: variant.margin || 0,
            prix_vente_ht: variant.priceMod || 0,
            stock: variant.stock || 0,
            summary: p.summary || '',
            warranty: p.warranty || '',
            weight: p.weight || 0,
            suppliers: p.suppliers && p.suppliers.length > 0 ? p.suppliers[0].name : ''
          });
        });
      } 
      // Produits simples
      else {
        expandedData.push({
          ref: p.ref,
          label: p.label,
          brand: p.brand,
          category: p.category,
          type_article: 'SIMPLE',
          variante_numero: '',
          variante_options: '',
          prix_achat_ht: p.purchasePrice || 0,
          marge: p.margin || 0,
          prix_vente_ht: p.price || 0,
          stock: p.stock || 0,
          summary: p.summary || '',
          warranty: p.warranty || '',
          weight: p.weight || 0,
          suppliers: p.suppliers && p.suppliers.length > 0 ? p.suppliers[0].name : ''
        });
      }
    });
    
    const csv = window.Papa.unparse(expandedData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `catalogue_detaille_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleCSVUpload = (e) => {
    if (!window.Papa) return;
    const file = e.target.files[0];
    if (file) {
      window.Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          // Format détaillé : regrouper les variantes par produit de base
          const productGroups = {};
          let imported = [];
          
          results.data.forEach((row, index) => {
            const typeArticle = row.type_article;
              
              if (typeArticle === 'VARIANTE') {
                // Extraire la référence de base (enlever le suffixe après le dernier tiret)
                const fullRef = row.ref || '';
                const refParts = fullRef.split('-');
                const baseRef = refParts.slice(0, -1).join('-'); // Tout sauf la dernière partie
                const variantCode = refParts[refParts.length - 1]; // Dernière partie
                
                if (!productGroups[baseRef]) {
                  productGroups[baseRef] = {
                    baseRef,
                    label: row.label,
                    brand: row.brand,
                    category: row.category,
                    summary: row.summary || '',
                    warranty: row.warranty || '2 ans',
                    weight: parseFloat(row.weight) || 0,
                    suppliers: row.suppliers ? [{ name: row.suppliers }] : [],
                    isMatrix: true,
                    variants: [],
                    options: [],
                    stockMap: {}
                  };
                }
                
                // Analyser variante_options pour extraire nom et valeur
                const variantOptions = row.variante_options || '';
                const optionParts = variantOptions.split(':').map(s => s.trim());
                const optionName = optionParts[0] || 'Option';
                const optionValue = optionParts[1] || variantCode;
                
                // Ajouter à options si pas déjà présent
                let option = productGroups[baseRef].options.find(o => o.name === optionName);
                if (!option) {
                  option = { name: optionName, values: [] };
                  productGroups[baseRef].options.push(option);
                }
                
                // Ajouter la valeur si pas déjà présente
                if (!option.values.find(v => v.label === optionValue)) {
                  option.values.push({
                    label: optionValue,
                    priceMod: parseFloat(row.prix_vente_ht) || 0,
                    purchasePrice: parseFloat(row.prix_achat_ht) || 0,
                    margin: parseFloat(row.marge) || 1.3
                  });
                }
                
                // Ajouter au stockMap
                productGroups[baseRef].stockMap[optionValue] = parseInt(row.stock) || 0;
                
                // Ajouter à variants (pour compatibilité)
                productGroups[baseRef].variants.push({
                  id: `v-${Date.now()}-${index}`,
                  options: { [optionName]: optionValue },
                  stock: parseInt(row.stock) || 0,
                  priceMod: parseFloat(row.prix_vente_ht) || 0,
                  purchasePrice: parseFloat(row.prix_achat_ht) || 0,
                  margin: parseFloat(row.marge) || 1.3
                });
                
              } else if (typeArticle === 'SIMPLE') {
                // Produit simple
                const simpleProduct = {
                  id: `imp-${Date.now()}-${index}`,
                  ref: row.ref || `REF-TEMP-${Date.now()}-${index}`,
                  label: row.label,
                  price: parseFloat(row.prix_vente_ht) || 0,
                  purchasePrice: parseFloat(row.prix_achat_ht) || 0,
                  margin: parseFloat(row.marge) || 1.3,
                  brand: row.brand || 'N/A',
                  category: row.category || 'Général',
                  stock: parseInt(row.stock) || 0,
                  summary: row.summary || '',
                  warranty: row.warranty || '2 ans',
                  weight: parseFloat(row.weight) || 0,
                  suppliers: row.suppliers ? [{ name: row.suppliers }] : [],
                  images: [],
                  variants: [],
                  isMatrix: false,
                  description: ''
                };
                imported.push(simpleProduct);
              }
            });
            
            // Convertir les groupes en produits
            Object.values(productGroups).forEach((group, idx) => {
              imported.push({
                id: `imp-${Date.now()}-${idx}`,
                ref: group.baseRef,
                label: group.label,
                price: 0, // Prix de base (calculé depuis options)
                purchasePrice: 0,
                margin: 1.3,
                brand: group.brand,
                category: group.category,
                stock: Object.values(group.stockMap).reduce((acc, s) => acc + s, 0),
                summary: group.summary,
                warranty: group.warranty,
                weight: group.weight,
                suppliers: group.suppliers,
                images: [],
                variants: group.variants,
                isMatrix: group.isMatrix,
                options: group.options,
                stockMap: group.stockMap,
                description: ''
              });
            });
          
          // Suite : traiter les produits importés
          if (imported.length > 0) {
            // Filtrer les doublons de 'ref' au sein même de l'import pour être cohérent avec la DB
            const uniqueImported = [];
            const seenRefs = new Set();
            for (const p of imported) {
              if (!seenRefs.has(p.ref)) {
                uniqueImported.push(p);
                seenRefs.add(p.ref);
              }
            }

            // Mise à jour locale immédiate
            setProducts(prev => {
              const importedRefs = new Set(uniqueImported.map(p => p.ref));
              const filteredPrev = prev.filter(p => !importedRefs.has(p.ref));
              return [...uniqueImported, ...filteredPrev];
            });
            
            // Synchronisation avec Supabase
            const syncImported = async () => {
              let successCount = 0;
              try {
                const dbProducts = uniqueImported.map(p => mapProductToDb(p));
                
                // Utiliser des paquets plus petits (10) car certains articles ont des images base64 très lourdes
                const chunkSize = 10;
                for (let i = 0; i < dbProducts.length; i += chunkSize) {
                  const chunk = dbProducts.slice(i, i + chunkSize);
                  const { data, error } = await supabase
                    .from('products')
                    .upsert(chunk, { onConflict: 'ref' })
                    .select();
                  
                  if (error) {
                    console.error(`Error syncing chunk ${i/chunkSize + 1}:`, error);
                    showToast(`Erreur au paquet ${i/chunkSize + 1} : ${error.message}`, "warning");
                    continue;
                  }

                  if (data && data.length > 0) {
                    successCount += data.length;
                    const syncedProducts = data.map(mapProductFromDb);
                    setProducts(prev => {
                      return prev.map(p => {
                        const synced = syncedProducts.find(sp => sp.ref === p.ref);
                        return synced || p;
                      });
                    });
                  }
                }
                
                if (successCount === uniqueImported.length) {
                  showToast(`${successCount} articles importés et synchronisés avec succès`);
                } else {
                  showToast(`${successCount}/${uniqueImported.length} articles synchronisés`, "warning");
                }
              } catch (error) {
                console.error('Error syncing imported products:', error.message);
                showToast(`Erreur critique de synchronisation : ${error.message}`, "error");
              }
            };
            
            syncImported();
          } else {
            showToast(`Aucun article trouvé dans le fichier.`);
          }
        }
      });
    }
  };

  const handleExportOrders = (format) => {
    if (format === 'json') {
      const blob = new Blob([JSON.stringify(orders, null, 2)], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob); link.download = 'commandes.json'; link.click();
    } else if (format === 'csv' && window.Papa) {
      const csv = window.Papa.unparse(orders);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob); link.download = 'commandes.csv'; link.click();
    }
  };

  if (typeof window !== 'undefined') window.handleExportOrders = handleExportOrders;

  const categoriesList = useMemo(() => {
    // Utilise exactement les mêmes catégories que productCategories (admin)
    // pour maintenir la cohérence entre le menu utilisateur et l'admin
    return ['All', ...productCategories];
  }, [productCategories]);

  const filteredOrders = useMemo(() => {
    const q = (orderSearchQuery || '').toLowerCase();
    const safeOrders = Array.isArray(orders) ? orders : [];
    
    // Déduplication par ID pour éviter les erreurs de clés React et les bugs de tri
    const uniqueOrders = [];
    const seenIds = new Set();
    safeOrders.forEach(o => {
      if (o && o.id && !seenIds.has(o.id)) {
        seenIds.add(o.id);
        uniqueOrders.push(o);
      }
    });

    const filtered = uniqueOrders.filter(order => {
      // Filtre par statut
      if (orderFilterStatus === 'received' && order.invoicePDF) return false;
      if (orderFilterStatus === 'invoiced' && !order.invoicePDF) return false;

      if (!q) return true;
      const matchId = (order.id || '').toLowerCase().includes(q);
      const matchDate = (order.date || '').toLowerCase().includes(q);
      const matchItems = order.items?.some(it => (it.label || '').toLowerCase().includes(q));
      return matchId || matchDate || matchItems;
    });

    const parseDate = (d) => {
      if (!d) return 0;
      if (d instanceof Date) return d.getTime();
      if (typeof d !== 'string') return 0;
      
      // Format DD/MM/YYYY (fr-FR)
      const parts = d.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        const date = new Date(year, month - 1, day);
        return isNaN(date.getTime()) ? 0 : date.getTime();
      }
      
      // Fallback standard
      const parsed = new Date(d);
      return isNaN(parsed.getTime()) ? 0 : parsed.getTime();
    };

    const parsePrice = (p) => {
      if (typeof p === 'number') return p;
      if (!p) return 0;
      const cleaned = String(p).replace(/[^\d.,-]/g, '').replace(',', '.');
      const val = parseFloat(cleaned);
      return isNaN(val) ? 0 : val;
    };

    return [...filtered].sort((a, b) => {
      let comparison = 0;
      if (orderSortBy === 'date-desc') comparison = parseDate(b.date) - parseDate(a.date);
      else if (orderSortBy === 'date-asc') comparison = parseDate(a.date) - parseDate(b.date);
      else if (orderSortBy === 'price-desc') comparison = parsePrice(b.totalTTC) - parsePrice(a.totalTTC);
      else if (orderSortBy === 'price-asc') comparison = parsePrice(a.totalTTC) - parsePrice(b.totalTTC);
      
      // Si égalité ou erreur de parsing, on garde l'ordre original (le plus récent d'abord)
      if (!comparison || isNaN(comparison)) {
        return uniqueOrders.indexOf(a) - uniqueOrders.indexOf(b);
      }
      return comparison;
    });
  }, [orders, orderSearchQuery, orderSortBy, orderFilterStatus]);

  const filteredProducts = useMemo(() => {
    const safeProducts = Array.isArray(products) ? products : [];
    
    // Filtrer uniquement par flash sale si nécessaire (le reste est géré par Supabase)
    let result = safeProducts;
    if (filterCategory === 'flash') {
      result = safeProducts.filter(p => isFlashSaleActive(p));
    }
    
    // Tri (côté client)
    if (sortBy === 'price-asc') result = [...result].sort((a, b) => (a.price || 0) - (b.price || 0));
    if (sortBy === 'price-desc') result = [...result].sort((a, b) => (b.price || 0) - (a.price || 0));
    if (sortBy === 'brand') result = [...result].sort((a, b) => (a.brand || '').localeCompare(b.brand || ''));
    
    return result;
  }, [products, filterCategory, sortBy, isFlashSaleActive]);

  // Force view change - wrapper function to ensure view changes work
  const forceViewChange = useCallback((newView) => {
    setView(newView);
  }, [setView]);

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-slate-900' : 'bg-slate-50'}`}>
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
          <p className={`font-black uppercase tracking-widest text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Initialisation de DeepVision Hub...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 font-sans selection:bg-blue-200 selection:text-blue-900 ${isDarkMode ? 'bg-slate-900 text-slate-200' : 'bg-[#F8FAFC] text-slate-900'}`}>
      <ToastContainer toasts={toasts} />
      <AccountModal 
        isOpen={showAccountModal}
        onClose={() => setShowAccountModal(false)}
        buyerInfo={buyerInfo}
        setBuyerInfo={setBuyerInfo}
      />
      <CartDrawer 
        isCartOpen={isCartOpen} 
        setIsCartOpen={setIsCartOpen} 
        cart={Array.isArray(cart) ? cart : []} 
        updateCartQuantity={updateCartQuantity} 
        removeFromCart={removeFromCart} 
        setView={forceViewChange} 
        cartTotal={cartTotal} 
        formatPrice={formatPrice}
      />
      
      <Navbar 
        setView={forceViewChange} 
        view={view}
        isAdmin={isAdmin} 
        toggleDarkMode={toggleDarkMode} 
        isDarkMode={isDarkMode} 
        setIsCartOpen={setIsCartOpen} 
        cartCount={cartCount} 
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        products={Array.isArray(products) ? products : []}
        openProduct={openProduct}
        compareCount={Array.isArray(compareList) ? compareList.length : 0}
        currency={currency}
        setCurrency={setCurrency}
        currencies={CURRENCIES}
        formatPrice={formatPrice}
        isLoggedIn={isLoggedIn}
        setIsLoggedIn={handleLogout}
        setShowLoginModal={setShowLoginModal}
        setShowAccountModal={setShowAccountModal}
        buyerInfo={buyerInfo}
      />

      <main className="w-full px-3.5 md:px-5 lg:px-7 pt-20 md:pt-28 lg:pt-32 min-h-screen">
        <ChunkErrorBoundary>
          <Suspense fallback={
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          }>
        {view === 'home' && (
          <HomeView 
            setView={setView} 
            products={Array.isArray(products) ? products : []} 
            favorites={Array.isArray(favorites) ? favorites : []} 
            toggleFavorite={toggleFavorite} 
            addToCart={addToCart} 
            openProduct={openProduct} 
            recentlyViewed={Array.isArray(recentlyViewed) ? recentlyViewed : []} 
            handleShare={handleShare}
            toggleCompare={toggleCompare}
            compareList={Array.isArray(compareList) ? compareList : []}
            formatPrice={formatPrice}
            isAdmin={isAdmin}
            isFlashSaleActive={isFlashSaleActive}
            isFlashSaleComingSoon={isFlashSaleComingSoon}
          />
        )}

        {view === 'catalog' && (
          <CatalogView 
            searchQuery={searchQuery} 
            setSearchQuery={setSearchQuery} 
            sortBy={sortBy} 
            setSortBy={setSortBy} 
            filterCategory={filterCategory} 
            setFilterCategory={setFilterCategory} 
            categoriesList={categoriesList} 
            displayMode={displayMode} 
            setDisplayMode={setDisplayMode} 
            filteredProducts={filteredProducts} 
            favorites={Array.isArray(favorites) ? favorites : []} 
            toggleFavorite={toggleFavorite} 
            addToCart={addToCart} 
            openProduct={openProduct} 
            handleShare={handleShare}
            allProducts={Array.isArray(products) ? products : []}
            toggleCompare={toggleCompare}
            compareList={Array.isArray(compareList) ? compareList : []}
            formatPrice={formatPrice}
            isAdmin={isAdmin}
            isFlashSaleActive={isFlashSaleActive}
            isFlashSaleComingSoon={isFlashSaleComingSoon}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            navigationMode={navigationMode}
            setNavigationMode={setNavigationMode}
            totalProducts={totalProducts}
            productsPerPage={PRODUCTS_PER_PAGE}
          />
        )}

        {view === 'compare' && (
          <CompareView 
            compareList={Array.isArray(compareList) ? compareList : []} 
            setCompareList={setCompareList} 
            addToCart={addToCart} 
            openProduct={openProduct} 
            setView={setView} 
            formatPrice={formatPrice}
          />
        )}

        {view === 'favorites' && (
          <div className="w-full py-12 px-3.5 md:px-5 lg:px-7 animate-in fade-in duration-500 text-left">
            <h2 className="text-4xl font-black mb-8 tracking-tighter text-slate-900 dark:text-white">Mes Favoris</h2>
            {favorites.length === 0 ? (
              <div className="bg-slate-50 dark:bg-slate-900/40 rounded-2xl p-12 text-center border dark:border-slate-800">
                <p className="text-slate-500">Vous n'avez pas encore de produits favoris.</p>
                <button onClick={() => setView('catalog')} className="mt-6 bg-blue-600 text-white px-8 py-3 rounded-xl font-bold">Parcourir le catalogue</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {favorites.map(product => (
                  <ProductCard 
                    key={product.id}
                    product={product}
                    favorites={favorites}
                    toggleFavorite={toggleFavorite}
                    addToCart={addToCart}
                    openProduct={openProduct}
                    handleShare={handleShare}
                    toggleCompare={toggleCompare}
                    isComparing={compareList.some(p => p.id === product.id)}
                    formatPrice={formatPrice}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {view === 'product-detail' && (
          <ProductDetailView 
            product={selectedProduct} 
            setView={setView} 
            addToCart={addToCart} 
            handleShare={handleShare} 
            formatPrice={formatPrice}
            favorites={favorites}
            toggleFavorite={toggleFavorite}
            isAdmin={isAdmin}
            isFlashSaleActive={isFlashSaleActive}
            isFlashSaleComingSoon={isFlashSaleComingSoon}
            flashSaleSettings={flashSaleSettings}
          />
        )}

        {view === 'success' && (
          <SuccessView
            name={lastBuyerName || ''}
            email={lastBuyerEmail || ''}
            orderRef={lastOrderRef || ''}
            date={lastOrderDate || ''}
            shipping={lastOrderShippingLabel || ''}
            total={lastOrderTotal || ''}
            hasOnOrder={(() => {
              const order = (Array.isArray(orders) ? orders : []).find(o => o.id === lastOrderRef);
              return order ? Boolean(order.acceptedOnOrder) : false;
            })()}
            reminderDate={(() => {
              const order = (Array.isArray(orders) ? orders : []).find(o => o.id === lastOrderRef);
              return order?.reminder?.date || null;
            })()}
            onReturnHome={() => { setView('home'); setLastOrderRef(null); setLastBuyerName(null); setLastBuyerEmail(null); setLastOrderDate(null); setLastOrderShippingLabel(null); setLastOrderTotal(null); }}
            onDownloadPDF={() => {
              const order = (Array.isArray(orders) ? orders : []).find(o => o.id === lastOrderRef);
              const itemsSnapshot = order ? order.items : [];
              const accepted = order ? Boolean(order.acceptedOnOrder) : false;
              const reminder = order ? order.reminder : null;
              generatePDF(lastOrderRef, { name: lastBuyerName, company: buyerInfo.company, email: lastBuyerEmail, address: buyerInfo.address, role: buyerInfo.role, phone: buyerInfo.phone }, itemsSnapshot, { createOrder: false, acceptedOnOrder: accepted, reminder: reminder });
            }}
          />
        )}

        {view === 'my-orders' && (
          <div className="w-full py-12 px-3.5 md:px-5 lg:px-7 animate-in fade-in duration-500 text-left">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
              <h2 className="text-4xl font-black tracking-tighter text-slate-900 dark:text-white">Mes Commandes</h2>
              <div className="flex flex-col sm:flex-row gap-4 flex-1 max-w-2xl">
                <div className="relative flex-1">
                  <input 
                    type="text" 
                    placeholder="Rechercher un produit, une réf..." 
                    className="w-full bg-slate-100 dark:bg-slate-800 border-none rounded-2xl py-3 pl-12 pr-4 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    value={orderSearchQuery}
                    onChange={(e) => setOrderSearchQuery(e.target.value)}
                  />
                  <Search className="absolute left-4 top-3.5 text-slate-400 w-5 h-5" />
                </div>
                <div className="relative min-w-[160px]">
                  <select 
                    value={orderFilterStatus}
                    onChange={(e) => setOrderFilterStatus(e.target.value)}
                    className="w-full bg-slate-100 dark:bg-slate-800 border-none rounded-2xl py-3 pl-10 pr-4 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all appearance-none font-bold text-sm"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="received">En attente</option>
                    <option value="invoiced">Facturées</option>
                  </select>
                  <ShieldCheck className="absolute left-3.5 top-3.5 text-slate-400 w-4 h-4 pointer-events-none" />
                </div>
                <div className="relative min-w-[200px]">
                  <select 
                    value={orderSortBy}
                    onChange={(e) => setOrderSortBy(e.target.value)}
                    className="w-full bg-slate-100 dark:bg-slate-800 border-none rounded-2xl py-3 pl-10 pr-4 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all appearance-none font-bold text-sm"
                  >
                    <option value="date-desc">Plus récentes</option>
                    <option value="date-asc">Plus anciennes</option>
                    <option value="price-desc">Montant décroissant</option>
                    <option value="price-asc">Montant croissant</option>
                  </select>
                  <ArrowUpDown className="absolute left-3.5 top-3.5 text-slate-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>
            </div>

            {orders.length === 0 ? (
              <div className="bg-slate-50 dark:bg-slate-900/40 rounded-2xl p-12 text-center border dark:border-slate-800">
                <p className="text-slate-500">Vous n'avez pas encore passé de commande.</p>
                <button onClick={() => setView('catalog')} className="mt-6 bg-blue-600 text-white px-8 py-3 rounded-xl font-bold">Découvrir le catalogue</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {filteredOrders.map((order, idx) => (
                  <div key={`${order.id}-${idx}`} className="bg-white dark:bg-slate-900/40 p-6 rounded-2xl border dark:border-slate-800 flex flex-col md:flex-row md:items-center justify-between hover:border-blue-500/50 transition-all group gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="text-xs font-black text-blue-600 uppercase tracking-widest">{order.id}</div>
                        <div className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${order.invoicePDF ? 'bg-blue-500/10 text-blue-500' : 'bg-emerald-500/10 text-emerald-500'}`}>
                          {order.invoicePDF ? 'Payée' : 'Reçue'}
                        </div>
                      </div>
                      <div className="text-lg font-bold text-slate-900 dark:text-white">{order.date}</div>
                      <div className="text-sm text-slate-500 mt-1">
                        {order.items?.length || 0} article(s) : <span className="italic">{order.items?.map(it => it.label).join(', ').substring(0, 60)}...</span>
                      </div>
                      <div className="text-sm font-black text-slate-700 dark:text-slate-300 mt-1">{formatPrice(order.totalTTC)}</div>
                    </div>
                    <div className="flex gap-3">
                      <button 
                        onClick={() => {
                          setTrackingOrderId(order.id);
                          setView('order-tracking');
                        }}
                        className="flex-1 md:flex-none bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-600 hover:text-white transition-all"
                      >
                        Suivre
                      </button>
                      <button 
                        onClick={() => {
                          generatePDF(order.id, order.buyer, order.items, { createOrder: false, acceptedOnOrder: order.acceptedOnOrder, reminder: order.reminder });
                        }}
                        className="flex-1 md:flex-none bg-blue-600/10 text-blue-600 px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-600 hover:text-white transition-all"
                      >
                        Devis PDF
                      </button>
                      {order.invoicePDF && (
                        <a 
                          href={order.invoicePDF}
                          download={`Facture_${order.id}.pdf`}
                          className="flex-1 md:flex-none bg-emerald-600 text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-emerald-700 transition-all flex items-center justify-center gap-2"
                        >
                          <ShoppingBag className="w-4 h-4" /> Facture PDF
                        </a>
                      )}
                    </div>
                  </div>
                ))}
                {filteredOrders.length === 0 && orders.length > 0 && (
                  <div className="p-12 text-center text-slate-500">Aucune commande ne correspond à votre recherche.</div>
                )}
              </div>
            )}
          </div>
        )}

        {view === 'order-tracking' && (
          <OrderTracking 
            orders={orders} 
            orderId={trackingOrderId} 
            onBack={() => setView('my-orders')} 
            onDownloadPDF={(order) => generatePDF(order.id, order.buyer, order.items, { createOrder: false, acceptedOnOrder: order.acceptedOnOrder, reminder: order.reminder })}
            formatPrice={formatPrice}
          />
        )}


        {view === 'cart' && (
          <CartView 
            cart={Array.isArray(cart) ? cart : []} 
            updateCartQuantity={updateCartQuantity} 
            removeFromCart={removeFromCart} 
            cartTotal={cartTotal} 
            buyerInfo={buyerInfo} 
            setBuyerInfo={setBuyerInfo} 
            isSendingEmail={isSendingEmail} 
            generatePDF={generatePDF}
            sendOrderEmail={sendOrderEmail}
            shippingFee={shippingFee}
            setShippingFee={setShippingFee}
            shippingPrices={shippingPrices}
            formatPrice={formatPrice}
            isLoggedIn={isLoggedIn}
            setShowLoginModal={setShowLoginModal}
          />
        )}



        {view === 'admin' && (isAdmin ? (
          <div className="w-full py-6 md:py-8 lg:py-12 px-3.5 md:px-5 lg:px-7 space-y-6 md:space-y-8 lg:space-y-12 animate-in fade-in duration-500 text-slate-900 dark:text-white text-left">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-slate-900 dark:text-white">
              <div className="space-y-1 w-full md:w-auto">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl md:text-2xl lg:text-4xl font-black tracking-tight uppercase">Administration</h2>
                  {/* Bouton hamburger mobile uniquement */}
                  <button 
                    onClick={() => setAdminMenuOpen(!adminMenuOpen)}
                    className="md:hidden p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                  >
                    <Menu className="w-5 h-5" />
                  </button>
                </div>
                {/* Onglets desktop - masqués sur mobile */}
                <div className="hidden md:flex gap-3 md:gap-4 lg:gap-6 pt-2 overflow-x-auto no-scrollbar">
                  <button 
                    onClick={() => setAdminTab('dashboard')}
                    className={`text-[9px] md:text-[10px] font-black uppercase tracking-[0.15em] md:tracking-[0.2em] transition-all whitespace-nowrap ${adminTab === 'dashboard' ? 'text-blue-600 border-b-2 border-blue-600 pb-1' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    Tableau de Bord
                  </button>
                  <button 
                    onClick={() => setAdminTab('articles')}
                    className={`text-[10px] font-black uppercase tracking-[0.2em] transition-all whitespace-nowrap ${adminTab === 'articles' ? 'text-blue-600 border-b-2 border-blue-600 pb-1' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    Base Articles
                  </button>
                  <button 
                    onClick={() => setAdminTab('stocks')}
                    className={`text-[10px] font-black uppercase tracking-[0.2em] transition-all flex items-center gap-2 whitespace-nowrap ${adminTab === 'stocks' ? 'text-blue-600 border-b-2 border-blue-600 pb-1' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    Gestion des Stocks
                    {hasLowStock && <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />}
                  </button>
                  <button 
                    onClick={() => setAdminTab('suppliers')}
                    className={`text-[10px] font-black uppercase tracking-[0.2em] transition-all whitespace-nowrap ${adminTab === 'suppliers' ? 'text-blue-600 border-b-2 border-blue-600 pb-1' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    Annuaire Fournisseurs
                  </button>
                  <button 
                    onClick={() => {
                      setAdminTab('clients');
                      setClientSearchTerm('');
                    }}
                    className={`text-[10px] font-black uppercase tracking-[0.2em] transition-all whitespace-nowrap ${adminTab === 'clients' ? 'text-blue-600 border-b-2 border-blue-600 pb-1' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    Clients
                  </button>
                </div>
                
                {/* Menu mobile dropdown */}
                {adminMenuOpen && (
                  <div className="md:hidden absolute left-2 right-2 top-32 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border dark:border-slate-700 z-50 overflow-hidden animate-in slide-in-from-top-4 duration-200">
                    <button 
                      onClick={() => { setAdminTab('dashboard'); setAdminMenuOpen(false); }}
                      className={`w-full px-6 py-4 text-left font-bold text-sm transition-all ${adminTab === 'dashboard' ? 'bg-blue-600 text-white' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                    >
                      Tableau de Bord
                    </button>
                    <button 
                      onClick={() => { setAdminTab('articles'); setAdminMenuOpen(false); }}
                      className={`w-full px-6 py-4 text-left font-bold text-sm transition-all ${adminTab === 'articles' ? 'bg-blue-600 text-white' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                    >
                      Base Articles
                    </button>
                    <button 
                      onClick={() => { setAdminTab('stocks'); setAdminMenuOpen(false); }}
                      className={`w-full px-6 py-4 text-left font-bold text-sm transition-all flex items-center justify-between ${adminTab === 'stocks' ? 'bg-blue-600 text-white' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                    >
                      Gestion des Stocks
                      {hasLowStock && <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />}
                    </button>
                    <button 
                      onClick={() => { setAdminTab('suppliers'); setAdminMenuOpen(false); }}
                      className={`w-full px-6 py-4 text-left font-bold text-sm transition-all ${adminTab === 'suppliers' ? 'bg-blue-600 text-white' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                    >
                      Annuaire Fournisseurs
                    </button>
                    <button 
                      onClick={() => { setAdminTab('clients'); setClientSearchTerm(''); setAdminMenuOpen(false); }}
                      className={`w-full px-6 py-4 text-left font-bold text-sm transition-all ${adminTab === 'clients' ? 'bg-blue-600 text-white' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                    >
                      Clients
                    </button>
                  </div>
                )}
              </div>
              <div className="flex gap-2 md:gap-4 w-full md:w-auto">
                <button onClick={() => setIsChangingCreds(true)} className="flex-1 md:flex-none bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-4 md:px-6 py-2.5 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-blue-600 hover:text-white transition-all shadow-sm text-xs md:text-sm">
                  <ShieldCheck className="w-4 h-4" /> Sécurité
                </button>
                <button onClick={logoutAdmin} className="flex-1 md:flex-none bg-red-50 dark:bg-red-900/20 text-red-600 px-4 md:px-6 py-2.5 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-red-100 transition-all shadow-sm text-xs md:text-sm">
                  <LogOut className="w-4 h-4" /> Quitter
                </button>
              </div>
            </div>

            {adminTab === 'suppliers' ? (
              <SuppliersView 
                products={Array.isArray(products) ? products : []} 
                onUpdateProducts={updateProducts} 
                orders={Array.isArray(orders) ? orders : []}
                clients={Array.isArray(clients) ? clients : []}
                onViewClient={(term) => {
                  setAdminTab('clients');
                  setClientSearchTerm(term);
                }}
                formatPrice={formatPrice}
                onSaveSupplier={saveSupplier}
                onDeleteSupplier={deleteSupplier}
                onSyncSuppliers={syncSuppliersFromProducts}
              />
            ) : adminTab === 'stocks' ? (
              <StockManagement 
                products={Array.isArray(products) ? products : []} 
                onUpdateStock={handleUpdateStock} 
                onBulkUpdateStock={handleGranularBulkStockUpdate}
                formatPrice={formatPrice}
              />
            ) : adminTab === 'articles' ? (
              <ArticlesBaseView 
                products={Array.isArray(products) ? products : []}
                selectedProductIds={selectedProductIds}
                toggleAllSelection={toggleAllSelection}
                toggleProductSelection={toggleProductSelection}
                duplicateProduct={duplicateProduct}
                setEditingProduct={setEditingProduct}
                setIsAddingProduct={setIsAddingProduct}
                deleteProduct={deleteProduct}
                deleteSelectedProducts={deleteSelectedProducts}
                handleBulkStockUpdate={handleBulkStockUpdate}
                handleCSVUpload={handleCSVUpload}
                handleExportCSVDetailed={handleExportCSVDetailed}
                toggleFlashSale={toggleFlashSale}
                updateFlashDiscount={updateFlashDiscount}
                toggleCategoryFlashSale={toggleCategoryFlashSale}
                renameCategory={renameCategory}
                deleteCategory={deleteCategory}
                mergeCategories={mergeCategories}
                formatPrice={formatPrice}
                isFlashSaleActive={isFlashSaleActive}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                navigationMode={navigationMode}
                setNavigationMode={setNavigationMode}
              />
            ) : adminTab === 'clients' ? (
              <ClientsList
                initialSearchTerm={clientSearchTerm}
                clients={clients}
                setClients={setClients}
                saveClient={saveClient}
                saveClients={saveClients}
                updateOrder={updateOrder}
                orders={orders}
                setOrders={setOrders}
                onNewOrder={(client) => {
                  setBuyerInfo({
                    name: client.name || client.nom,
                    company: client.company || client.societe,
                    email: client.email,
                    phone: client.phone || client.telephone,
                    address: client.address || (client.addresses?.[0]?.value || ''),
                    role: client.contacts?.[0]?.role || 'Client'
                  });
                  setView('catalog');
                  setAdminTab('dashboard');
                }}
                formatPrice={formatPrice}
              />
            ) : (
              <AdminDashboard 
                orders={Array.isArray(orders) ? orders : []} 
                products={Array.isArray(products) ? products : []}
                clients={Array.isArray(clients) ? clients : []}
                shippingPrices={shippingPrices}
                setShippingPrices={setShippingPrices}
                formatPrice={formatPrice}
                flashSaleSettings={flashSaleSettings}
                setFlashSaleSettings={setFlashSaleSettings}
              />
            )}
            {(isAddingProduct || editingProduct) && (
              <ProductFormModal
                product={editingProduct}
                categories={productCategories}
                allProducts={products}
                suppliers={suppliers}
                onClose={() => {
                  setEditingProduct(null);
                  setIsAddingProduct(false);
                }}
                onSave={saveProduct}
              />
            )}
            
            {isChangingCreds && (
              <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[32px] shadow-2xl p-8 space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-black dark:text-white">Sécurité Admin</h3>
                    <button onClick={() => setIsChangingCreds(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full dark:text-white"><X /></button>
                  </div>
                  <form onSubmit={updateAdminCreds} className="space-y-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase text-slate-400">Nouvel Identifiant</label>
                      <input name="newUser" required defaultValue={adminCredentials.user} className="w-full bg-slate-50 dark:bg-slate-800 p-4 rounded-2xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase text-slate-400">Nouveau Mot de Passe</label>
                      <input name="newPass" type="password" required defaultValue={adminCredentials.pass} className="w-full bg-slate-50 dark:bg-slate-800 p-4 rounded-2xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase text-slate-400">Email de Récupération</label>
                      <input name="newEmail" type="email" required defaultValue={adminCredentials.recoveryEmail} className="w-full bg-slate-50 dark:bg-slate-800 p-4 rounded-2xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div className="flex items-center gap-3">
                      <button type="submit" className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black shadow-lg hover:bg-blue-700 transition-all">Enregistrer les modifications</button>
                      <SavedBadge visible={savedCreds} />
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="max-w-md mx-auto py-20 animate-in zoom-in-95 duration-300">
            <div className="bg-white dark:bg-slate-900 p-12 rounded-[48px] border dark:border-slate-800 shadow-2xl text-center text-slate-900 dark:text-white">
              <h2 className="text-3xl font-black dark:text-white mb-10 text-xs opacity-50 tracking-[0.3em] uppercase">
                {showRecovery ? 'Récupération' : 'Accès Console'}
              </h2>
              
              {!showRecovery ? (
                <form onSubmit={handleLogin} className="space-y-6">
                  <input name="user" required placeholder="Utilisateur" className="w-full bg-slate-50 dark:bg-slate-800 rounded-2xl py-5 px-8 dark:text-white border-none outline-none focus:ring-2 focus:ring-blue-500 font-bold shadow-inner" />
                  <input name="pass" required type="password" placeholder="••••••••" className="w-full bg-slate-50 dark:bg-slate-800 rounded-2xl py-5 px-8 dark:text-white border-none outline-none focus:ring-2 focus:ring-blue-500 font-bold shadow-inner" />
                  <button type="submit" className="w-full bg-blue-600 text-white py-5 rounded-2xl font-black shadow-xl active:scale-95 transition-all flex items-center justify-center gap-2">Connexion</button>
                  <div className="flex flex-col gap-4 pt-2">
                    <button type="button" onClick={() => setShowRecovery(true)} className="text-[10px] text-slate-400 hover:text-blue-500 transition-colors font-bold uppercase tracking-widest">Identifiants oubliés ?</button>
                    <div className="h-px bg-slate-100 dark:bg-slate-800 w-1/2 mx-auto"></div>
                    <button 
                      type="button" 
                      onClick={() => { setView('home'); setShowLoginModal(true); setAuthMode('register'); }} 
                      className="text-xs text-blue-600 hover:text-blue-700 font-black uppercase tracking-wider"
                    >
                      Créer un compte client
                    </button>
                  </div>
                </form>
              ) : isResettingPassword ? (
                <form onSubmit={handlePasswordReset} className="space-y-6">
                  <div className="space-y-2">
                    <p className="text-sm font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">Nouveau mot de passe</p>
                    <p className="text-xs text-slate-500 leading-relaxed">Veuillez choisir un nouveau mot de passe pour votre compte.</p>
                  </div>
                  <input name="newPass" type="password" required placeholder="Nouveau mot de passe" className="w-full bg-slate-50 dark:bg-slate-800 rounded-2xl py-5 px-8 dark:text-white border-none outline-none focus:ring-2 focus:ring-blue-500 font-bold shadow-inner" />
                  <input name="confirmPass" type="password" required placeholder="Confirmer le mot de passe" className="w-full bg-slate-50 dark:bg-slate-800 rounded-2xl py-5 px-8 dark:text-white border-none outline-none focus:ring-2 focus:ring-blue-500 font-bold shadow-inner" />
                  <button type="submit" disabled={isSendingEmail} className="w-full bg-blue-600 text-white py-5 rounded-2xl font-black shadow-xl active:scale-95 transition-all flex items-center justify-center gap-2">
                    {isSendingEmail ? <><Loader2 className="w-5 h-5 animate-spin" /> Mise à jour...</> : 'Changer le mot de passe'}
                  </button>
                  <button type="button" onClick={resetRecovery} className="text-xs text-slate-400 hover:text-blue-500 transition-colors font-bold uppercase tracking-widest">Annuler</button>
                </form>
              ) : isVerifyingCode ? (
                <form onSubmit={verifyRecoveryCode} className="space-y-6">
                  <div className="space-y-2">
                    <p className="text-sm font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">Code envoyé !</p>
                    <p className="text-xs text-slate-500 leading-relaxed">Consultez votre boîte mail pour récupérer votre code de sécurité à 6 chiffres.</p>
                  </div>
                  <div className="relative">
                    <input name="code" required placeholder="······" maxLength="6" autoComplete="off" className="w-full bg-slate-50 dark:bg-slate-800 rounded-2xl py-6 px-8 dark:text-white border-none outline-none focus:ring-2 focus:ring-blue-500 font-black text-center text-3xl tracking-[0.5em] shadow-inner" />
                  </div>
                  <button type="submit" className="w-full bg-blue-600 text-white py-5 rounded-2xl font-black shadow-xl hover:bg-blue-700 active:scale-95 transition-all">Valider le code</button>
                  <div className="pt-4">
                    <button type="button" onClick={() => { setIsVerifyingCode(false); setRecoveryCode(''); }} className="text-[10px] text-slate-400 hover:text-blue-500 transition-colors font-black uppercase tracking-[0.2em]">Renvoyer un nouveau code</button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleRecovery} className="space-y-6">
                  <div className="space-y-2">
                    <p className="text-sm text-slate-500 leading-relaxed">
                      Entrez votre adresse e-mail pour recevoir un lien ou un code de récupération.
                    </p>
                  </div>
                  <input 
                    type="email" 
                    required 
                    value={recoveryEmailInput}
                    onChange={(e) => setRecoveryEmailInput(e.target.value)}
                    placeholder="votre@email.com" 
                    className="w-full bg-slate-50 dark:bg-slate-800 rounded-2xl py-5 px-8 dark:text-white border-none outline-none focus:ring-2 focus:ring-blue-500 font-bold shadow-inner" 
                  />
                  <button 
                    type="submit"
                    disabled={isSendingEmail} 
                    className="w-full bg-slate-900 dark:bg-slate-700 text-white py-5 rounded-2xl font-black shadow-xl active:scale-95 transition-all flex items-center justify-center gap-2"
                  >
                    {isSendingEmail ? <><Loader2 className="w-5 h-5 animate-spin" /> Envoi...</> : 'Récupérer mes accès'}
                  </button>
                  <button type="button" onClick={resetRecovery} className="text-xs text-slate-400 hover:text-blue-500 transition-colors font-bold uppercase tracking-widest">Retour à la connexion</button>
                </form>
              )}
            </div>
          </div>
        ))}
          </Suspense>
        </ChunkErrorBoundary>
      </main>
      
      {showLoginModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[32px] shadow-2xl p-8 space-y-6 animate-in zoom-in-95 duration-300">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-black dark:text-white">
                {authMode === 'login' ? 'Connexion Client' : 'Créer un compte'}
              </h3>
              <button onClick={() => setShowLoginModal(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full dark:text-white"><X /></button>
            </div>
            
            <div className="space-y-4">
              <p className="text-sm text-slate-500">
                {authMode === 'login' 
                  ? 'Connectez-vous pour accéder à vos commandes et favoris.' 
                  : 'Rejoignez-nous pour un suivi simplifié de vos commandes.'}
              </p>

              <form onSubmit={async (e) => {
                e.preventDefault();
                const fd = new FormData(e.target);
                const email = fd.get('email');
                const password = fd.get('password');
                
                if (authMode === 'login') {
                  const { error } = await supabase.auth.signInWithPassword({
                    email,
                    password,
                  });
                  
                  if (error) {
                    showToast("Erreur de connexion : " + error.message, "error");
                  } else {
                    showToast("Connexion réussie", "success");
                    setShowLoginModal(false);
                  }
                } else {
                  // Mode Inscription
                  const firstName = fd.get('firstName');
                  const lastName = fd.get('lastName');
                  const name = `${firstName} ${lastName}`;
                  const company = fd.get('company');
                  const phone = fd.get('phone');
                  const address = fd.get('address');
                  const postalCode = fd.get('postalCode');
                  const city = fd.get('city');
                  const country = fd.get('country');

                  const { data, error } = await supabase.auth.signUp({
                    email,
                    password,
                    options: {
                      data: {
                        first_name: firstName,
                        last_name: lastName,
                        full_name: name,
                        company: company,
                        phone: phone,
                        address: address,
                        postal_code: postalCode,
                        city: city,
                        country: country,
                        role: 'client'
                      }
                    }
                  });

                  if (error) {
                    showToast("Erreur d'inscription : " + error.message, "error");
                  } else {
                    // Save to clients table for admin visibility
                    try {
                      await saveClient({
                        firstName,
                        lastName,
                        name,
                        company,
                        email,
                        phone,
                        address,
                        postalCode,
                        city,
                        country,
                        role: 'Client'
                      });
                    } catch (err) {
                      console.error('Failed to save client:', err);
                    }
                    
                    // Vérifier si l'utilisateur est automatiquement connecté (pas de confirmation email requise)
                    if (data?.session) {
                      // Session créée automatiquement - l'utilisateur est connecté
                      showToast("Bienvenue ! Votre compte est créé et vous êtes connecté.", "success");
                      setShowLoginModal(false);
                      setView('home'); // Redirection vers la page d'accueil
                    } else {
                      // Confirmation par email requise
                      showToast("Compte créé ! Vérifiez vos emails pour confirmer votre compte.", "success");
                      setShowLoginModal(false);
                    }
                  }
                }
              }} className="space-y-4">
                
                {authMode === 'register' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400">Prénom *</label>
                        <input name="firstName" required placeholder="Jean" className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400">Nom *</label>
                        <input name="lastName" required placeholder="Dupont" className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase text-slate-400">Société</label>
                      <input name="company" placeholder="Ma Société" className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase text-slate-400">Téléphone</label>
                      <input name="phone" placeholder="01 02 03 04 05" className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase text-slate-400">Adresse</label>
                      <input name="address" placeholder="123 Rue de Paris..." className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400">Code Postal</label>
                        <input name="postalCode" placeholder="75001" className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400">Ville</label>
                        <input name="city" placeholder="Paris" className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400">Pays</label>
                        <input name="country" placeholder="France" defaultValue="France" className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                      </div>
                    </div>
                  </>
                )}

                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-slate-400">Adresse Email</label>
                  <input id="client-login-email" name="email" type="email" required placeholder="votre@email.com" className="w-full bg-slate-50 dark:bg-slate-800 p-4 rounded-2xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500" />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <label className="text-[10px] font-black uppercase text-slate-400">Mot de passe</label>
                    {authMode === 'login' && (
                      <button 
                        type="button" 
                        onClick={async () => {
                          const email = document.getElementById('client-login-email')?.value;
                          if (!email) {
                            showToast("Veuillez entrer votre email d'abord", "error");
                            return;
                          }
                          setIsSendingEmail(true);
                          const { error } = await supabase.auth.resetPasswordForEmail(email, {
                            redirectTo: window.location.origin,
                          });
                          setIsSendingEmail(false);
                          if (error) {
                            showToast("Erreur : " + error.message, "error");
                          } else {
                            showToast("Lien de réinitialisation envoyé !", "success");
                          }
                        }}
                        className="text-[9px] text-blue-600 hover:underline font-bold uppercase tracking-wider"
                      >
                        Mot de passe oublié ?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <input 
                      id="password-input"
                      name="password" 
                      type={showPassword ? "text" : "password"} 
                      required 
                      placeholder="••••••••" 
                      className="w-full bg-slate-50 dark:bg-slate-800 p-4 pr-12 rounded-2xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500" 
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                      title={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button type="submit" className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black shadow-lg hover:bg-blue-700 transition-all">
                  {authMode === 'login' ? 'Se connecter' : 'Créer mon compte'}
                </button>
              </form>

              <div className="pt-4 border-t dark:border-slate-800 flex flex-col gap-3">
                <button 
                  onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
                  className="text-xs text-blue-600 hover:underline font-bold text-center"
                >
                  {authMode === 'login' ? "Pas encore de compte ? S'inscrire" : "Déjà un compte ? Se connecter"}
                </button>
                
                <button 
                  onClick={handleGuestLogin} 
                  className="text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 font-black uppercase tracking-widest text-center"
                >
                  Continuer en tant qu'invité
                </button>

                <div className="h-px bg-slate-100 dark:bg-slate-800 w-1/4 mx-auto my-1"></div>
                
                <button 
                  onClick={() => { setView('admin'); setShowLoginModal(false); }}
                  className="text-[9px] text-slate-400 hover:text-blue-600 font-black uppercase tracking-[0.2em] text-center transition-colors"
                >
                  Accès Administration
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />

      {showScrollTop && (
        <button 
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-[200] bg-blue-600 text-white p-4 rounded-2xl shadow-2xl hover:bg-blue-700 hover:scale-110 active:scale-95 transition-all animate-in fade-in slide-in-from-bottom-4 duration-300"
          aria-label="Retour en haut"
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
